<template>
	<div class="auto-campaign-sequences-wrapper">
		<div class="auto-campaign-sequences-page">
			<div class="create-campaign-container container">
				<!-- Header -->
				<div class="page-header d-flex align-items-center my-4">
					<router-link :to="{ name: 'autoCampaigns' }" class="back-btn me-3" title="Back to Auto Campaigns">
						<i class="fad fa-arrow-left"></i>
					</router-link>
					<div>
						<h2 class="page-title">Edit Auto Campaign with Sequences</h2>
						<p class="page-subtitle">Update your automated sequences and scheduling.</p>
					</div>
				</div>
				<div class="d-flex justify-content-end mb-3" v-if="campaignId">
					<router-link :to="{ name: 'campaignEmailReport', params: { campaignType: 'sequence', campaignId } }" class="btn btn-outline-primary btn-sm">
						<i class="fad fa-envelope-open-text me-2"></i>
						Email Report
					</router-link>
				</div>

				<!-- Loading State -->
				<div v-if="loading" class="loading-state">
					<div class="text-center py-5">
						<i class="fad fa-spinner fa-spin fa-3x text-primary mb-3"></i>
						<h5>Loading Campaign...</h5>
						<p class="text-muted">Please wait while we load your campaign data.</p>
					</div>
				</div>

				<!-- Main Content -->
				<div v-else class="main-content-wrapper">
					<!-- Timeline Section (Full Width) -->
					<div class="timeline-section timeline-full-width">
						<div class="timeline-container timeline-centered">
							<!-- Timeline -->
							<div class="sequence-timeline">
								<!-- Start Trigger -->
								<div class="timeline-start">
									<!-- Active Campaign Compact Banner -->
									<div v-if="isCampaignActive" class="campaign-automation-badge">
										<div class="automation-content">
											<div class="automation-header">
												<i class="fad fa-rocket me-2"></i>
												<span class="automation-title">Campaign Running Automatically</span>
												<i class="fad fa-check-circle text-success ms-2"></i>
											</div>
											<div class="automation-description">
												System sends sequences at scheduled times → Recipients processed in chunks → Real-time tracking → No action needed
											</div>
										</div>
									</div>

									<div class="timeline-campaign-card" @click="selectCampaignInfo" :class="{ active: selectedItemId === 'campaign' }">
										<div class="campaign-card-header">
											<div class="campaign-icon">
												<i class="fad fa-envelopes-bulk"></i>
											</div>
											<div class="campaign-info">
												<div class="campaign-title">{{ campaign.name || 'Untitled Campaign' }}</div>
												<div class="campaign-subtitle">{{ campaign.description || 'Click to edit campaign details' }}</div>
											</div>
											<div class="campaign-badges">
												<div class="stats-row">
													<div class="stat-item-inline">
														<i class="fad fa-list-ol me-1"></i>
														<span class="stat-number">{{ sequenceItems.length }}</span>
														<span class="stat-label">Steps</span>
													</div>
													<div class="stat-item-inline">
														<i class="fad fa-users me-1"></i>
														<span class="stat-number">{{ campaign.items_count || 0 }}</span>
														<span class="stat-label">Recipients</span>
													</div>
												</div>
												<span class="badge badge-outline" :class="getStatusBadgeOutlineClass(campaign.status)">{{ campaign.status || 'Draft' }}</span>
											</div>
										</div>
										<div class="campaign-card-actions mt-1">
											<button
												v-if="sequenceItems.length > 0 && !isCampaignActive"
												class="btn btn-sm position-relative"
												:class="getCheckButtonClass"
												@click.stop="checkCampaignReadiness"
												:disabled="loading || !campaignId"
											>
												<i class="fad me-1" :class="getCheckButtonIcon"></i>
												Check
												<span
													v-if="totalErrorCount > 0"
													class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger error-badge-small"
												>
													{{ totalErrorCount }}
													<span class="visually-hidden">errors</span>
												</span>
											</button>
											<button
												v-if="sequenceItems.length > 0 && !isCampaignActive"
												class="btn btn-success btn-sm"
												@click.stop="startCampaign"
												:disabled="loading || !campaignId || hasErrors || !hasCheckedStatus"
											>
												<i class="fad fa-rocket me-1"></i>
												Start Campaign
											</button>
										</div>
									</div>

									<!-- Campaign Mode Badge -->
									<div class="campaign-mode-badge" :class="getCampaignModeClass(campaign.mode)">
										<i class="fad" :class="getCampaignModeIcon(campaign.mode)"></i>
										<span class="mode-text">{{ getCampaignModeLabel(campaign.mode) }}</span>
									</div>

									<!-- Inline Campaign Configuration -->
									<div v-if="selectedItemId === 'campaign'" class="inline-config-full-width campaign-config">
										<div class="config-header">
											<h5>Configure Campaign</h5>
											<button class="btn btn-primary btn-sm" @click="saveCampaign" :disabled="saving">
												<i class="fad me-1" :class="saving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
												{{ saving ? 'Saving...' : 'Save' }}
											</button>
										</div>
										<div class="config-form-inline">
											<div class="row">
												<div class="col-md-6">
													<label class="form-label">Campaign Name</label>
													<input type="text" class="form-control" v-model="campaign.name" placeholder="Enter campaign name" />
												</div>

												<div class="col-md-6">
													<label class="form-label">Mode</label>
													<select class="form-select" v-model="campaign.mode" :disabled="hasStartedSequences">
														<option value="test">Test</option>
														<option value="live">Live</option>
													</select>
													<div v-if="hasStartedSequences" class="form-text text-muted">
														<i class="fad fa-lock me-1"></i>
														Mode cannot be changed after sequences have started
													</div>
												</div>
											</div>
											<div class="row mt-3">
												<div class="col-md-6">
													<label class="form-label" for="campaignUtm">UTM Campaign</label>
													<utm-campaign-input id="campaignUtm" v-model="campaign.utm_campaign" />
												</div>
											</div>
											<div class="row mt-3">
												<div class="col-md-6">
													<label class="form-label">Description</label>
													<textarea class="form-control" rows="2" v-model="campaign.description" placeholder="Describe your sequence campaign"></textarea>
												</div>
												<div class="col-md-6">
													<label class="form-label">Campaign Items</label>
													<div class="items-info-section">
														<div class="items-count">
															<span class="items-count-number">{{ campaign.items_count || 0 }}</span>
															<span class="items-count-label">items</span>
														</div>
														<button class="btn btn-outline-secondary btn-sm" @click="showItemsModal" :disabled="!campaign.items_count">
															<i class="fad fa-list me-1"></i>
															View Items
														</button>
													</div>
												</div>
											</div>
										</div>
									</div>

									<div class="timeline-connector"></div>
								</div>

								<!-- Timeline Items -->
								<div class="timeline-items">
									<div v-for="(item, index) in sequenceItems" :key="item.id" class="timeline-item">
										<!-- Enhanced Timeline Card -->
										<div
											class="timeline-sequence-card-enhanced"
											:class="[
												getItemTypeClass(item.type),
												{ 'has-errors': hasSequenceErrors(item), 'campaign-active': isCampaignActive, 'status-completed': item.status === 'completed' },
											]"
											@click="isCampaignActive ? selectSequenceForItems(item) : selectItem(item.id)"
										>
											<div class="card-header-enhanced">
												<div class="card-icon-enhanced">
													<i v-if="item.type === 'email'" class="fad fa-envelope"></i>
													<i v-else-if="item.type === 'postcard'" class="fad fa-image"></i>
													<i v-else-if="item.type === 'letter'" class="fad fa-file-pdf"></i>
												</div>
												<div class="card-content-enhanced">
													<div class="card-title-enhanced">
														{{ item.name || `${getItemTypeLabel(item.type)} ${index + 1}` }}
													</div>
													<div class="card-subtitle-enhanced">{{ item.description || `Click to configure this ${item.type}` }}</div>

													<!-- Header Info Row -->
													<div class="card-header-info">
														<!-- Status Badge (Centered) -->
														<span class="info-badge status-badge centered-status" :class="'status-' + (item.status || 'pending')">
															<i class="fad fa-circle me-1"></i>
															{{ (item.status || 'pending').charAt(0).toUpperCase() + (item.status || 'pending').slice(1) }}
														</span>

														<!-- Cost Display (Only for sending/completed) -->
														<span v-if="(item.status === 'sending' || item.status === 'completed') && item.cost_per_item" class="info-badge cost-badge">
															${{ parseFloat(item.cost).toFixed(2) }}
														</span>

														<!-- Recipients Count -->
														<span class="info-badge recipients-badge" v-if="campaign && campaign.campaign_items_count">
															<i class="fad fa-users me-1"></i>
															{{ formatNumber(campaign.campaign_items_count) }} recipients
														</span>

														<!-- Cancel Sequence Button -->
														<div v-if="canCancelSequence(item)">
															<button
																class="btn btn-outline-warning btn-xs cancel-btn-small rounded"
																@click.stop="cancelFullSequence(item)"
																:disabled="item.cancelling"
																title="Cancel entire sequence"
															>
																<i v-if="item.cancelling" class="fad fa-spinner fa-spin me-1"></i>
																<i v-else class="fad fa-ban me-1"></i>
																<span v-if="!item.cancelling">Cancel</span>
																<span v-else>Cancelling...</span>
															</button>
														</div>
													</div>
												</div>
												<div class="card-type-badge">
													{{ getItemTypeLabel(item.type) }}
												</div>
											</div>
											<div v-if="!isCampaignActive" class="card-actions-enhanced">
												<button class="btn btn-circle" @click.stop="moveItem(index, -1)" :disabled="index === 0" title="Move Up">
													<i class="fad fa-chevron-up"></i>
												</button>
												<button class="btn btn-circle" @click.stop="moveItem(index, 1)" :disabled="index === sequenceItems.length - 1" title="Move Down">
													<i class="fad fa-chevron-down"></i>
												</button>
												<button
													v-if="item.type === 'postcard' || item.type === 'letter'"
													class="btn btn-circle btn-primary"
													@click.stop="testSequence(item)"
													:disabled="item.testing"
													title="Test Sequence"
												>
													<i v-if="item.testing" class="fad fa-spinner fa-spin"></i>
													<i v-else class="fad fa-flask"></i>
												</button>
												<button class="btn btn-circle btn-danger" @click.stop="removeItem(index)" title="Remove">
													<i class="fad fa-trash"></i>
												</button>
											</div>
											<div v-else class="card-status-active"></div>
										</div>

										<!-- Inline Item Configuration - Email -->
										<div
											v-if="selectedItemId === item.id && item.type === 'email' && !isCampaignActive"
											class="inline-config-full-width item-config email-config"
										>
											<div class="config-header">
												<h5>Configure Email Step</h5>
												<div class="item-type-badge email-node">
													<i class="fad fa-envelope me-1"></i>
													Email
												</div>
											</div>
											<div class="config-form-inline">
												<div class="row mb-3">
													<div class="col-md-6">
														<label class="form-label">Step Name</label>
														<input type="text" class="form-control" v-model="item.name" placeholder="Enter email step name" />
													</div>
													<div class="col-md-6">
														<label class="form-label">Status</label>
														<input type="text" class="form-control" v-model="item.status" readonly />
													</div>
												</div>
												<div class="row mb-3">
													<div class="col-md-12">
														<label class="form-label">Description</label>
														<textarea class="form-control" rows="2" v-model="item.description" placeholder="Describe this email step"></textarea>
													</div>
												</div>
												<div class="row mb-3">
													<div class="col-md-6">
														<label class="form-label">Email Provider</label>
														<select class="form-select" v-model="item.email_provider_config_id">
															<option :value="null">Select Provider</option>
															<option v-for="provider in emailProviderConfigs" :key="provider.id" :value="provider.id">
																{{ provider.name }} ({{ provider.provider }})
															</option>
														</select>
													</div>
													<div class="col-md-6">
														<label class="form-label">Email Template</label>
														<select class="form-select" v-model="item.email_template_id">
															<option :value="null">Select Template</option>
															<option v-for="template in emailTemplates" :key="template.id" :value="template.id">{{ template.name }}</option>
														</select>
													</div>
												</div>
												<div class="row mb-3">
													<div class="col-md-6">
														<label class="form-label">Recipient Source</label>
														<select class="form-select" v-model="item.email_recipient_source">
															<option value="municipality_email_only">Municipality Email Only</option>
															<option value="contacts_only">All Contact Emails</option>
															<option value="municipality_plus_contacts">Municipality + Contacts</option>
														</select>
													</div>
													<div class="col-md-6">
														<label class="form-label">Schedule Type</label>
														<select class="form-select" v-model="item.email_schedule_type">
															<option value="immediate">Immediate</option>
															<option value="at_scheduled_time">At Sequence Start</option>
															<option value="fixed_delay">Fixed Delay</option>
															<option value="after_previous_step_sent">After Previous Step Sent</option>
														</select>
													</div>
												</div>
												<div class="row mb-3" v-if="item.email_schedule_type === 'fixed_delay'">
													<div class="col-md-6">
														<label class="form-label">Delay Value</label>
														<input type="number" min="0" class="form-control" v-model.number="item.email_delay_value" />
													</div>
													<div class="col-md-6">
														<label class="form-label">Delay Unit</label>
														<select class="form-select" v-model="item.email_delay_unit">
															<option value="minutes">Minutes</option>
															<option value="hours">Hours</option>
															<option value="days">Days</option>
														</select>
													</div>
												</div>
												<div class="alert alert-info">
													<i class="fad fa-info-circle me-2"></i>
													Templates are rendered from the shared email queue when the campaign starts.
												</div>

												<!-- Footer Actions -->
												<div class="config-footer">
													<div class="d-flex justify-content-between align-items-center">
														<div class="pricing-display">
															<span v-if="item.type && item.mail_type" class="pricing-info">
																<span class="price-value">${{ calculateItemCost(item).toFixed(3) }}</span> per item
															</span>
														</div>
														<div class="d-flex gap-2">
															<button type="button" class="btn btn-outline-secondary" @click="selectedItemId = null">Cancel</button>
															<button type="button" class="btn btn-primary" @click="saveSequenceItem(item)">
																<i class="fad me-1 fa-save"></i>
																Update
															</button>
														</div>
													</div>
												</div>
											</div>
										</div>

										<!-- Inline Item Configuration - Postcard -->
										<div
											v-else-if="selectedItemId === item.id && item.type === 'postcard' && !isCampaignActive"
											class="inline-config-full-width item-config postcard-config"
										>
											<div class="config-header">
												<h5>Configure Postcard Sequence</h5>
												<div class="item-type-badge postcard-node">
													<i class="fad fa-image me-1"></i>
													Postcard
												</div>
											</div>
											<div class="config-form-inline">
												<div class="row">
													<!-- Left 1/3 - Image Uploads with Previews -->
													<div class="col-md-4">
														<div class="postcard-uploads">
															<!-- Front Image Upload -->
															<div class="postcard-upload-section mb-3">
																<label class="form-label fw-bold">Front Image</label>
																<div class="postcard-upload-area" @click="triggerPostcardUpload(item, 'front')">
																	<div v-if="item.postcard_front_url" class="postcard-preview">
																		<img
																			:src="getPostcardImageUrl(item.postcard_front_url)"
																			alt="Front Preview"
																			class="postcard-preview-image"
																		/>
																		<div class="postcard-preview-overlay">
																			<button
																				type="button"
																				class="btn btn-sm btn-danger postcard-remove-btn"
																				@click.stop="removePostcardImage(item, 'front')"
																			>
																				<i class="fad fa-trash"></i>
																			</button>
																		</div>
																	</div>
																	<div v-else class="postcard-upload-placeholder">
																		<i class="fad fa-image fa-2x mb-2"></i>
																		<p class="mb-1">Click to upload</p>
																		<p class="small text-muted">Front image</p>
																	</div>
																</div>
															</div>

															<!-- Back Image Upload -->
															<div class="postcard-upload-section">
																<label class="form-label fw-bold">Back Image</label>
																<div class="postcard-upload-area" @click="triggerPostcardUpload(item, 'back')">
																	<div v-if="item.postcard_back_url" class="postcard-preview">
																		<img :src="getPostcardImageUrl(item.postcard_back_url)" alt="Back Preview" class="postcard-preview-image" />
																		<div class="postcard-preview-overlay">
																			<button
																				type="button"
																				class="btn btn-sm btn-danger postcard-remove-btn"
																				@click.stop="removePostcardImage(item, 'back')"
																			>
																				<i class="fad fa-trash"></i>
																			</button>
																		</div>
																	</div>
																	<div v-else class="postcard-upload-placeholder">
																		<i class="fad fa-image fa-2x mb-2"></i>
																		<p class="mb-1">Click to upload</p>
																		<p class="small text-muted">Back image</p>
																	</div>
																</div>
															</div>
														</div>
													</div>

													<!-- Right 2/3 - Configuration Fields -->
													<div class="col-md-8">
														<div class="postcard-config-fields">
															<div class="row mb-3">
																<div class="col-md-12">
																	<label class="form-label">Step Name</label>
																	<input type="text" class="form-control" v-model="item.name" placeholder="Enter postcard name" />
																</div>
															</div>
															<div class="row mb-3">
																<div class="col-md-12">
																	<label class="form-label">Description</label>
																	<textarea
																		class="form-control"
																		rows="3"
																		v-model="item.description"
																		placeholder="Describe this postcard step"
																	></textarea>
																</div>
															</div>
															<div class="row mb-3">
																<div class="col-md-6">
																	<label class="form-label">Size <span class="text-danger">*</span></label>
																	<select class="form-select" v-model="item.postcard_size">
																		<option value="">Select Size</option>
																		<option value="4x6">4x6</option>
																		<option value="6x9">6x9</option>
																		<option value="6x11">6x11</option>
																	</select>
																</div>
																<div class="col-md-6">
																	<label class="form-label">Mail Service <span class="text-danger">*</span></label>
																	<select class="form-select" v-model="item.mail_type">
																		<option value="">Select Mail Service</option>
																		<option value="usps_first_class">USPS First Class</option>
																		<option value="usps_standard">USPS Standard</option>
																	</select>
																</div>
															</div>
															<div class="row mb-3">
																<div class="col-md-6">
																	<label class="form-label">Sender <span class="text-danger">*</span></label>
																	<select class="form-select" v-model="item.sender">
																		<option value="">Select Sender</option>
																		<option v-for="sender in senders" :key="sender.name" :value="sender.name">{{ sender.name }}</option>
																	</select>
																</div>
																<div class="col-md-6">
																	<label class="form-label">Status</label>
																	<input type="text" class="form-control" v-model="item.status" readonly />
																</div>
															</div>

															<!-- Chunk Management Section -->
															<div class="row mt-4">
																<div class="col-12">
																	<h6 class="section-subtitle">Chunk Management</h6>
																</div>
															</div>
															<div class="row mt-3">
																<div class="col-md-4">
																	<label class="form-label">Chunk Size</label>
																	<input
																		type="number"
																		class="form-control"
																		v-model="item.chunk_size"
																		placeholder="100"
																		min="1"
																		@input="calculateTotalChunks(item)"
																	/>
																</div>
																<div class="col-md-4">
																	<label class="form-label">Total Chunks</label>
																	<input type="number" class="form-control" v-model="item.total_chunks" readonly />
																</div>
																<div class="col-md-4">
																	<label class="form-label">Chunk Time Gap</label>
																	<select class="form-select" v-model="item.chunk_time_gap">
																		<option value="">Select Time Gap</option>
																		<option value="15">15 minutes</option>
																		<option value="30">30 minutes</option>
																		<option value="60">60 minutes</option>
																		<option value="120">2 hours</option>
																		<option value="360">6 hours</option>
																		<option value="720">12 hours</option>
																		<option value="1440">1 day</option>
																		<option value="2880">2 days</option>
																		<option value="4320">3 days</option>
																		<option value="10080">1 week</option>
																	</select>
																</div>
															</div>
															<div class="row mt-3">
																<div class="col-md-6">
																	<label class="form-label">Sequence Start At</label>
																	<input
																		type="datetime-local"
																		class="form-control"
																		v-model="item.sequence_start_at"
																		:disabled="item.position !== 0"
																		@change="onSequenceStartAtChange"
																	/>
																	<div class="form-text">
																		<i class="fad fa-info-circle me-1"></i>
																		<span v-if="item.position === 0">Select date and time when sequence will start.</span
																		><span v-else>Auto-calculated based on sequence time gap.</span>
																	</div>
																</div>
																<div class="col-md-6">
																	<label class="form-label">Sequence Time Gap</label>
																	<select
																		class="form-select"
																		v-model="item.sequence_time_gap"
																		:disabled="item.position === 0"
																		@change="onSequenceTimeGapChange"
																	>
																		<option value="">Select Sequence Gap</option>
																		<option value="1">1 day</option>
																		<option value="5">5 days</option>
																		<option value="10">10 days</option>
																		<option value="30">30 days</option>
																		<option value="60">60 days</option>
																	</select>
																	<div class="form-text">
																		<i class="fad fa-info-circle me-1"></i>
																		<span v-if="item.position === 0">Not applicable for first sequence item.</span
																		><span v-else>Gap from previous sequence. Changes automatically update all following sequences.</span>
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>

												<!-- Footer Actions -->
												<div class="config-footer">
													<div class="d-flex justify-content-between align-items-center">
														<div class="pricing-display">
															<span v-if="item.type && item.mail_type" class="pricing-info">
																<span class="price-value">${{ calculateItemCost(item).toFixed(3) }}</span> per item
															</span>
														</div>
														<div class="d-flex gap-2">
															<button type="button" class="btn btn-outline-secondary" @click="selectedItemId = null">Cancel</button>
															<button type="button" class="btn btn-primary" @click="saveSequenceItem(item)">
																<i class="fad me-1 fa-save"></i>
																Update
															</button>
														</div>
													</div>
												</div>
											</div>
										</div>

										<!-- Inline Item Configuration - Letter -->
										<div
											v-else-if="selectedItemId === item.id && item.type === 'letter' && !isCampaignActive"
											class="inline-config-full-width item-config letter-config"
										>
											<div class="config-header">
												<h5>Configure Letter Step</h5>
												<div class="item-type-badge letter-node">
													<i class="fad fa-file-pdf me-1"></i>
													Letter
												</div>
											</div>
											<div class="config-form-inline">
												<!-- Main Configuration Row -->
												<div class="row">
													<!-- Left Column - Form Fields (8 cols) -->
													<div class="col-md-8">
														<div class="letter-config-fields">
															<div class="row mb-3">
																<div class="col-md-6">
																	<label class="form-label">Step Name</label>
																	<input type="text" class="form-control" v-model="item.name" placeholder="Enter letter step name" />
																</div>
																<div class="col-md-6">
																	<label class="form-label">Status</label>
																	<input type="text" class="form-control" v-model="item.status" readonly />
																</div>
															</div>
															<div class="row mb-3">
																<div class="col-md-12">
																	<label class="form-label">Description</label>
																	<textarea
																		class="form-control"
																		rows="2"
																		v-model="item.description"
																		placeholder="Describe this letter step"
																	></textarea>
																</div>
															</div>
															<div class="row mb-3">
																<div class="col-md-6">
																	<label class="form-label">Sender <span class="text-danger">*</span></label>
																	<select class="form-select" v-model="item.sender">
																		<option value="">Select Sender</option>
																		<option v-for="sender in senders" :key="sender.name" :value="sender.name">{{ sender.name }}</option>
																	</select>
																</div>
																<div class="col-md-6">
																	<label class="form-label">Mail Service <span class="text-danger">*</span></label>
																	<select class="form-select" v-model="item.mail_type">
																		<option value="">Select Mail Service</option>
																		<option value="usps_first_class">USPS First Class</option>
																		<option value="usps_standard">USPS Standard</option>
																	</select>
																</div>
															</div>
															<div class="row mb-3">
																<div class="col-md-4">
																	<label class="form-label">Color</label>
																	<select class="form-select" v-model="item.letter_color">
																		<option value="">Select</option>
																		<option value="1">Yes</option>
																		<option value="0">No</option>
																	</select>
																</div>
																<div class="col-md-4">
																	<label class="form-label">Double Sided</label>
																	<select class="form-select" v-model="item.letter_double_sided">
																		<option value="">Select</option>
																		<option value="1">Yes</option>
																		<option value="0">No</option>
																	</select>
																</div>
																<div class="col-md-4">
																	<label class="form-label">Extra Service</label>
																	<select class="form-select" v-model="item.letter_extra_service">
																		<option value="">None</option>
																		<option value="certified">Certified</option>
																		<option value="certified_return_receipt">Certified Return Receipt</option>
																	</select>
																</div>
															</div>
															<div class="row mb-3">
																<div class="col-md-12">
																	<label class="form-label">Address Placement <span class="text-danger">*</span></label>
																	<select class="form-select" v-model="item.letter_address_placement">
																		<option value="">Select Placement</option>
																		<option value="top_first_page">On Top of First Page</option>
																		<option value="insert_blank_page">On a Blank Page</option>
																	</select>
																</div>
															</div>
														</div>
													</div>

													<!-- Right Column - PDF Upload and Preview -->
													<div class="col-md-4">
														<div class="letter-pdf-section">
															<label class="form-label fw-bold">Letter PDF</label>
															<div class="letter-pdf-container">
																<div class="letter-upload-column">
																	<div class="letter-upload-area" @click="triggerLetterUpload(item)">
																		<div v-if="item.letter_url" class="letter-preview">
																			<div class="pdf-preview-container">
																				<i class="fad fa-file-pdf fa-2x text-danger mb-2"></i>
																				<p class="mb-1 fw-bold">{{ item.letter_pdf_name || 'Letter.pdf' }}</p>
																			</div>
																			<div class="letter-preview-overlay">
																				<button
																					type="button"
																					class="btn btn-sm btn-danger letter-remove-btn"
																					@click.stop="removeLetterPdf(item)"
																				>
																					<i class="fad fa-trash"></i>
																				</button>
																			</div>
																		</div>
																		<div v-else class="letter-upload-placeholder">
																			<i class="fad fa-file-pdf fa-2x mb-2"></i>
																			<p class="mb-1">Click to upload</p>
																		</div>
																	</div>
																</div>

																<!-- PDF Preview Area (1/3 width) -->
																<div v-if="item.letter_url && getLetterPdfUrl(item)" class="letter-preview-column">
																	<div class="pdf-preview-mini" :class="{ expanded: item.pdfExpanded }">
																		<div class="pdf-preview-header">
																			<span class="preview-label">Preview</span>
																			<button type="button" class="btn btn-sm btn-outline-primary expand-btn" @click="togglePdfPreview(item)">
																				<i class="fad" :class="item.pdfExpanded ? 'fa-compress' : 'fa-expand'"></i>
																			</button>
																		</div>
																		<div class="pdf-mini-viewer">
																			<div v-if="item.pdfLoading" class="pdf-loading-mini">
																				<i class="fad fa-spinner fa-spin"></i>
																			</div>
																			<div v-else class="pdf-viewer-wrapper-mini">
																				<iframe
																					v-if="item.pdfViewMethod === 'iframe'"
																					:src="getLetterPdfUrl(item)"
																					class="pdf-viewer-mini"
																					@load="onPdfLoad(item)"
																					@error="onPdfError(item)"
																				>
																				</iframe>
																				<object
																					v-else-if="item.pdfViewMethod === 'object'"
																					:data="getLetterPdfUrl(item)"
																					type="application/pdf"
																					class="pdf-viewer-mini"
																					@load="onPdfLoad(item)"
																					@error="onPdfError(item)"
																				>
																					<embed :src="getLetterPdfUrl(item)" type="application/pdf" class="pdf-viewer-mini" />
																				</object>
																				<div class="pdf-fallback-mini" v-else>
																					<div class="fallback-content-mini">
																						<i class="fad fa-file-pdf fa-2x pdf-icon"></i>
																						<p class="small">Preview not available</p>
																					</div>
																				</div>
																			</div>
																		</div>
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>

												<!-- Chunk Management Section - Full Width -->
												<div class="row mt-4">
													<div class="col-12">
														<h6 class="section-subtitle">Chunk Management</h6>
													</div>
												</div>

												<div class="row mt-3">
													<div class="col-md-4">
														<label class="form-label">Chunk Size</label>
														<input
															type="number"
															class="form-control"
															v-model="item.chunk_size"
															placeholder="100"
															min="1"
															@input="calculateTotalChunks(item)"
														/>
													</div>
													<div class="col-md-4">
														<label class="form-label">Total Chunks</label>
														<input type="number" class="form-control" v-model="item.total_chunks" readonly />
													</div>
													<div class="col-md-4">
														<label class="form-label">Chunk Time Gap</label>
														<select class="form-select" v-model="item.chunk_time_gap">
															<option value="">Select Time Gap</option>
															<option value="15">15 minutes</option>
															<option value="30">30 minutes</option>
															<option value="60">60 minutes</option>
															<option value="120">2 hours</option>
															<option value="360">6 hours</option>
															<option value="720">12 hours</option>
															<option value="1440">1 day</option>
															<option value="2880">2 days</option>
															<option value="4320">3 days</option>
															<option value="10080">1 week</option>
														</select>
													</div>
												</div>

												<div class="row mt-3">
													<div class="col-md-6">
														<label class="form-label">Sequence Start At</label>
														<input
															type="datetime-local"
															class="form-control"
															v-model="item.sequence_start_at"
															:disabled="item.position !== 0"
															@change="onSequenceStartAtChange"
														/>
														<div class="form-text">
															<i class="fad fa-info-circle me-1"></i>
															<span v-if="item.position === 0">Select date and time when sequence will start.</span
															><span v-else>Auto-calculated based on sequence time gap.</span>
														</div>
													</div>
													<div class="col-md-6">
														<label class="form-label">Sequence Time Gap</label>
														<select
															class="form-select"
															v-model="item.sequence_time_gap"
															:disabled="item.position === 0"
															@change="onSequenceTimeGapChange"
														>
															<option value="">Select Sequence Gap</option>
															<option value="1">1 day</option>
															<option value="5">5 days</option>
															<option value="10">10 days</option>
															<option value="30">30 days</option>
															<option value="60">60 days</option>
														</select>
														<div class="form-text">
															<i class="fad fa-info-circle me-1"></i>
															<span v-if="item.position === 0">Not applicable for first sequence item.</span
															><span v-else>Gap from previous sequence. Changes automatically update all following sequences.</span>
														</div>
													</div>
												</div>

												<!-- Footer Actions -->
												<div class="config-footer">
													<div class="d-flex justify-content-between align-items-center">
														<div class="pricing-display">
															<span v-if="item.type && item.mail_type" class="pricing-info">
																<span class="price-value">${{ calculateItemCost(item).toFixed(3) }}</span> per item
															</span>
														</div>
														<div class="d-flex gap-2">
															<button type="button" class="btn btn-outline-secondary" @click="selectedItemId = null">Cancel</button>
															<button type="button" class="btn btn-primary" @click="saveSequenceItem(item)">
																<i class="fad me-1 fa-save"></i>
																Update
															</button>
														</div>
													</div>
												</div>
											</div>
										</div>

										<!-- Inline Sequence Items View (for Active Campaign) -->
										<div v-if="selectedItemId === item.id && isCampaignActive" class="inline-config-full-width sequence-items-view">
											<div class="config-header">
												<div class="config-header-left">
													<h5>{{ item.name || 'Sequence' }} Items</h5>
													<div class="item-type-badge" :class="item.type + '-node'">
														<i class="fad" :class="getSequenceTypeIcon(item.type)"></i>
														{{ item.type.charAt(0).toUpperCase() + item.type.slice(1) }}
													</div>
												</div>
												<div class="config-header-right">
													<!-- Status Badge -->
													<span class="info-badge status-badge" :class="'status-' + (item.status || 'pending')">
														<i class="fad fa-circle me-1"></i>
														{{ (item.status || 'pending').charAt(0).toUpperCase() + (item.status || 'pending').slice(1) }}
													</span>

													<!-- Cost Display (Only for sending/completed) -->
													<span v-if="(item.status === 'sending' || item.status === 'completed') && item.cost_per_item" class="info-badge cost-badge">
														${{ parseFloat(item.cost).toFixed(2) }}
													</span>

													<!-- Start Date -->
													<span class="info-badge date-badge" v-if="item.sequence_start_at">
														<i class="fad fa-calendar me-1"></i>
														{{ formatDate(item.sequence_start_at) }}
													</span>

													<!-- Total Recipients -->
													<span class="info-badge recipients-badge">
														<i class="fad fa-users me-1"></i>
														{{ formatNumber(campaign.items_count || 0) }} recipients
													</span>
												</div>
											</div>

											<div class="config-form-inline">
												<!-- Loading State -->
												<div v-if="loadingSequenceItems" class="text-center py-4">
													<div class="spinner-border text-primary" role="status">
														<span class="visually-hidden">Loading...</span>
													</div>
													<p class="text-muted mt-2">Loading sequence items...</p>
												</div>

												<!-- Sequence Items Table -->
												<div v-else-if="currentSequenceItems.length > 0" class="table-responsive">
													<table class="table table-hover table-sm">
														<thead class="table-dark">
															<tr>
																<th>ID</th>
																<th>Recipient</th>
																<th>Scheduled At</th>
																<th>Sent At</th>
																<th>Expected Delivery</th>
																<th>Status</th>
																<th>Action</th>
															</tr>
														</thead>
														<tbody>
															<tr v-for="item in currentSequenceItems" :key="item.id">
																<td>{{ item.id }}</td>
																<td>
																	<div>
																		<strong>{{ item.name || 'N/A' }}</strong>
																		<br />
																		<small class="text-muted">{{ item.address || 'N/A' }}</small
																		>, <small class="text-muted">{{ item.city || 'N/A' }}</small
																		>,
																		<small class="text-muted">{{ item.state || 'N/A' }}</small>
																	</div>
																</td>
																<td>
																	<small>{{ formatServerDate(item.scheduled_at) }}</small>
																</td>
																<td>
																	<small>{{ item.sent_at ? formatServerDate(item.sent_at) : '-' }}</small>
																</td>
																<td>
																	<small>{{ item.expected_delivery_at ? formatCompactDate(item.expected_delivery_at) : '-' }}</small>
																</td>
																<td>
																	<span class="badge badge-sm" :class="getItemStatusBadgeClass(item.status)">
																		{{ item.status || 'pending' }}
																	</span>
																</td>
																<td>
																	<button
																		v-if="canCancelItem(item)"
																		class="btn btn-outline-warning btn-xs cancel-btn-small rounded"
																		@click="cancelSequenceItem(item)"
																		:disabled="item.cancelling"
																		title="Cancel this sequence item"
																	>
																		<i v-if="item.cancelling" class="fad fa-spinner fa-spin me-1"></i>
																		<i v-else class="fad fa-ban me-1"></i>
																		<span v-if="!item.cancelling">Cancel</span>
																		<span v-else>Cancelling...</span>
																	</button>
																	<span v-else class="text-muted small">-</span>
																</td>
															</tr>
														</tbody>
													</table>
												</div>

												<!-- No Items -->
												<div v-else class="text-center py-4 text-muted">
													<i class="fad fa-inbox fa-3x mb-3"></i>
													<p>No items found for this sequence.</p>
												</div>
											</div>

											<!-- Footer Actions (Outside scrollable area) -->
											<div class="config-footer">
												<div class="d-flex justify-content-between">
													<small class="text-muted">
														<i class="fad fa-info-circle me-1"></i>
														Campaign is active - items are read-only
													</small>
													<button type="button" class="btn btn-outline-secondary" @click="selectedItemId = null">
														<i class="fad fa-times me-1"></i>
														Close
													</button>
												</div>
											</div>
										</div>

										<!-- Sequence Start Date Display -->
										<div class="sequence-start-date" :class="[getSequenceTimeClass(item), getItemTypeClass(item.type)]">
											<i class="fad fa-calendar me-1"></i>
											<span>{{ getSequenceTimeDisplay(item) }}</span>
											<!-- Arrow and truck icon for delivery -->
											<div class="delivery-indicator">
												<i class="fad fa-arrow-right mx-2"></i>
												<i class="fad fa-truck"></i>
											</div>
										</div>

										<!-- Connector Line -->
										<div v-if="index < sequenceItems.length - 1 || sequenceItems.length > 0" class="timeline-connector"></div>
									</div>
								</div>

								<!-- Add Step Button -->
								<div v-if="!isCampaignActive" class="timeline-add-item">
									<div class="add-step-circle" @click="toggleAddDropdown" :class="{ active: showAddDropdown }">
										<i class="fad" :class="showAddDropdown ? 'fa-minus' : 'fa-plus'"></i>
									</div>

									<!-- Add Item Dropdown -->
									<div v-if="showAddDropdown" class="add-item-dropdown">
										<div class="dropdown-options">
											<button class="dropdown-option" @click="addSequenceItem('postcard')">
												<i class="fad fa-image me-2"></i>
												Postcard
											</button>
											<button class="dropdown-option" @click="addSequenceItem('letter')">
												<i class="fad fa-file-pdf me-2"></i>
												Letter
											</button>
											<button class="dropdown-option" @click="addSequenceItem('email')">
												<i class="fad fa-envelope me-2"></i>
												Email
											</button>
										</div>
									</div>

									<!-- Final Connector -->
									<div v-if="sequenceItems.length > 0 && !isCampaignActive" class="timeline-connector"></div>
								</div>

								<!-- End Node -->
								<div v-if="sequenceItems.length > 0" class="timeline-end">
									<div class="timeline-end-circle" :class="{ 'timeline-end-expanded': isCampaignActive }">
										<span v-if="!isCampaignActive">End</span>
										<div v-else class="d-flex align-items-center last-item-display">
											<i class="fad fa-flag-checkered text-success me-2"></i>
											<div class="last-item-text">
												<small class="fw-bold d-block campaign-ends-title">Campaign Ends</small>
												<small class="text-muted campaign-ends-date">{{ formatServerDate(getLastSequenceEndDate()) }}</small>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Campaign Items Modal -->
		<div class="modal fade" id="itemsModal" tabindex="-1" aria-labelledby="itemsModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="itemsModalLabel">Campaign Items ({{ campaign.items_count || 0 }})</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div v-if="loadingItems" class="text-center py-4">
							<i class="fad fa-spinner fa-spin fa-2x text-primary mb-3"></i>
							<p>Loading items...</p>
						</div>
						<div v-else-if="campaignItems.length > 0">
							<div class="table-responsive">
								<table class="table table-hover">
									<thead>
										<tr>
											<th>ID</th>
											<th>Name</th>
											<th>Email</th>
											<th>Status</th>
											<th>Created</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="item in campaignItems" :key="item.id">
											<td>{{ item.id }}</td>
											<td>{{ item.name || '-' }}</td>
											<td>{{ item.email || '-' }}</td>
											<td>
												<span class="badge" :class="getItemStatusClass(item.status)">
													{{ item.status || 'Unknown' }}
												</span>
											</td>
											<td>{{ formatDate(item.created_at) }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
						<div v-else class="text-center py-4 text-muted">
							<i class="fad fa-inbox fa-2x mb-3"></i>
							<p>No items found for this campaign.</p>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Bootstrap Offcanvas for Campaign Issues -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="campaignIssuesOffcanvas" aria-labelledby="campaignIssuesOffcanvasLabel">
			<div class="offcanvas-header">
				<h5 class="offcanvas-title" id="campaignIssuesOffcanvasLabel">
					<i class="fad fa-exclamation-triangle text-warning me-2"></i>
					Campaign Issues
				</h5>
				<button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>

			<div class="offcanvas-body">
				<!-- Campaign Level Errors -->
				<div v-if="campaignErrors.length > 0" class="error-section mb-4">
					<h6 class="error-section-title">
						<i class="fad fa-campaign me-1"></i>
						Campaign
						<span class="badge bg-danger ms-2">Not Ready</span>
					</h6>
					<div class="error-list">
						<div v-for="(error, index) in campaignErrors" :key="index" class="error-item">
							<i class="fad fa-exclamation-circle text-danger me-2"></i>
							{{ error.message }}
						</div>
					</div>
				</div>

				<!-- All Sequences with Status -->
				<div v-for="item in sequenceItems" :key="item.id" class="error-section mb-4">
					<h6 class="error-section-title">
						<i class="fad fa-layer-group me-1"></i>
						{{ item.name }}
						<span class="sequence-type-badge" :class="item.type">{{ item.type }}</span>
						<span v-if="hasSequenceErrors(item)" class="badge bg-danger ms-2">Not Ready</span>
						<span v-else class="badge bg-success ms-2">Ready</span>
					</h6>

					<!-- Show errors if any -->
					<div v-if="hasSequenceErrors(item)" class="error-list">
						<div v-for="(error, index) in sequenceErrors[item.id].errors" :key="index" class="error-item">
							<i class="fad fa-exclamation-circle text-danger me-2"></i>
							{{ error.message }}
						</div>
					</div>

					<!-- Show ready status if no errors -->
					<div v-else class="ready-status">
						<div class="d-flex align-items-center text-success">
							<i class="fad fa-check-circle me-2"></i>
							<span>All validations passed</span>
						</div>
					</div>
				</div>

				<!-- Overall Status Summary -->
				<div class="mt-4 p-3 rounded" :class="hasErrors ? 'bg-danger bg-opacity-10' : 'bg-success bg-opacity-10'">
					<div class="d-flex align-items-center">
						<i class="fad fa-2x me-3" :class="hasErrors ? 'fa-exclamation-triangle text-danger' : 'fa-check-circle text-success'"></i>
						<div>
							<h6 class="mb-1" :class="hasErrors ? 'text-danger' : 'text-success'">
								{{ hasErrors ? 'Campaign Not Ready' : 'Campaign Ready' }}
							</h6>
							<small class="text-muted">
								{{ hasErrors ? `${totalErrorCount} issue(s) need to be resolved` : 'All validations passed successfully' }}
							</small>
						</div>
					</div>
				</div>
			</div>

			<div class="offcanvas-footer p-3 border-top">
				<button type="button" class="btn btn-outline-primary btn-sm me-2" @click="checkCampaignReadiness">
					<i class="fad fa-sync me-1"></i>
					Re-check
				</button>
				<button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="offcanvas">Close</button>
			</div>
		</div>

		<!-- Start Campaign Modal -->
		<div class="modal fade" id="startCampaignModal" tabindex="-1" aria-labelledby="startCampaignModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-xl">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="startCampaignModalLabel">
							<i class="fad fa-play-circle text-success me-2"></i>
							Start Campaign
						</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>

					<div class="modal-body">
						<!-- Warning Text -->
						<div class="alert alert-warning mb-4" role="alert">
							<i class="fad fa-exclamation-triangle me-2"></i>
							<strong>Important:</strong> When start campaign is triggered we will create all sequence items with proper schedule dates, after that campaign can't be
							edited, campaign will work totally automated and will send items like they set in sequences, you will be able to follow here all items and sequences
							statuses and stats.
						</div>

						<!-- Campaign Mode Display -->
						<div class="mb-4">
							<h6>Campaign Mode:</h6>
							<span class="badge fs-6 px-3 py-2" :class="campaign.mode === 'live' ? 'bg-success' : 'bg-warning text-dark'">
								<i class="fad fa-broadcast-tower me-1"></i>
								{{ campaign.mode === 'live' ? 'LIVE' : 'TEST' }}
							</span>
						</div>

						<!-- Sequence Schedule List -->
						<div class="mb-4">
							<h6>Sequence Schedule Overview:</h6>
							<div class="table-responsive">
								<table class="table table-hover table-sm align-middle">
									<thead class="table-dark">
										<tr>
											<th scope="col" class="ps-2">Sequence</th>
											<th scope="col" class="text-center">Type</th>
											<th scope="col" class="text-center">Start Date</th>
											<th scope="col" class="text-center">End Date</th>
											<th scope="col" class="text-center">Chunks</th>
											<th scope="col" class="text-center">Recipients</th>
											<th scope="col" class="text-center">Gap</th>
											<th scope="col" class="text-center pe-2">Status</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="item in sequenceItems" :key="item.id">
											<td class="ps-2">
												<div>
													<strong class="d-block text-truncate" style="max-width: 150px" :title="item.name">{{ item.name }}</strong>
													<small class="text-muted text-truncate d-block" style="max-width: 150px" :title="item.description || 'No description'">{{
														item.description || 'No description'
													}}</small>
												</div>
											</td>
											<td class="text-center">
												<span class="badge badge-sm badge-outline-primary">
													<i class="fad" :class="getSequenceTypeIcon(item.type)"></i>
													{{ item.type.charAt(0).toUpperCase() + item.type.slice(1) }}
												</span>
											</td>
											<td class="text-center">
												<div>
													<small class="fw-bold text-primary d-block">{{ formatCompactDate(item.sequence_start_at) }}</small>
													<small class="text-muted">{{ getRelativeTime(item.sequence_start_at) }}</small>
												</div>
											</td>
											<td class="text-center">
												<div>
													<small class="fw-bold text-info d-block">{{ formatCompactDate(calculateEndDate(item)) }}</small>
													<small class="text-muted">{{ getRelativeTime(calculateEndDate(item)) }}</small>
												</div>
											</td>
											<td class="text-center">
												<span class="badge badge-sm badge-outline-info">
													{{ item.total_chunks || 0 }}
												</span>
											</td>
											<td class="text-center">
												<span class="badge badge-sm badge-outline-secondary">
													{{ campaign.items_count || 0 }}
												</span>
											</td>
											<td class="text-center">
												<span class="badge badge-sm badge-outline-warning">
													{{ formatCompactTimeGap(item.chunk_time_gap) }}
												</span>
											</td>
											<td class="text-center pe-2">
												<span v-if="hasSequenceErrors(item)" class="badge badge-sm badge-outline-danger">
													<i class="fad fa-times"></i>
												</span>
												<span v-else class="badge badge-sm badge-outline-success">
													<i class="fad fa-check"></i>
												</span>
											</td>
										</tr>
									</tbody>
								</table>
							</div>

							<!-- Summary Row -->
							<div class="row mt-3">
								<div class="col-md-12">
									<div class="card bg-light">
										<div class="card-body py-2">
											<div class="row text-center">
												<div class="col-md-3">
													<small class="text-muted d-block">Total Sequences</small>
													<strong class="text-primary">{{ sequenceItems.length }}</strong>
												</div>
												<div class="col-md-3">
													<small class="text-muted d-block">Total Chunks</small>
													<strong class="text-info">{{ getTotalChunks }}</strong>
												</div>
												<div class="col-md-3">
													<small class="text-muted d-block">Total Recipients</small>
													<strong class="text-success">{{ getTotalRecipients }}</strong>
												</div>
												<div class="col-md-3">
													<small class="text-muted d-block">Ready Status</small>
													<strong :class="hasErrors ? 'text-danger' : 'text-success'">
														{{ hasErrors ? 'Issues Found' : 'All Ready' }}
													</strong>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div class="modal-footer">
						<div class="flex-grow-1 text-start">
							<div v-if="startingCampaign" class="d-flex align-items-center text-primary">
								<div class="spinner-border spinner-border-sm me-2" role="status">
									<span class="visually-hidden">Loading...</span>
								</div>
								<span class="fw-medium">{{ campaignStartStatus }}</span>
							</div>
						</div>
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="startingCampaign">
							<i class="fad fa-times me-1"></i>
							Cancel
						</button>
						<button type="button" class="btn btn-success btn-lg rounded-pill" @click="confirmStartCampaign" :disabled="startingCampaign">
							<span v-if="startingCampaign">
								<span class="spinner-border spinner-border-sm me-2" role="status">
									<span class="visually-hidden">Loading...</span>
								</span>
								Starting...
							</span>
							<span v-else>
								<i class="fad fa-play me-1"></i>
								Start Campaign
							</span>
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Sequence Items Modal -->
		<div class="modal fade" id="sequenceItemsModal" tabindex="-1" aria-labelledby="sequenceItemsModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-xl">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="sequenceItemsModalLabel">
							<i class="fad fa-list me-2" :class="currentSequence ? getSequenceTypeIcon(currentSequence.type) : 'fa-list'"></i>
							{{ currentSequence ? currentSequence.name : 'Sequence Items' }}
						</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>

					<div class="modal-body">
						<!-- Sequence Info -->
						<div v-if="currentSequence" class="mb-4">
							<div class="row">
								<div class="col-md-6">
									<div class="card bg-light">
										<div class="card-body py-2">
											<div class="row">
												<div class="col-6">
													<small class="text-muted d-block">Type</small>
													<span class="badge badge-outline-primary">
														<i class="fad" :class="getSequenceTypeIcon(currentSequence.type)"></i>
														{{ currentSequence.type.charAt(0).toUpperCase() + currentSequence.type.slice(1) }}
													</span>
												</div>
												<div class="col-6">
													<small class="text-muted d-block">Status</small>
													<span class="badge" :class="getSequenceStatusBadgeClass(currentSequence.status)">
														{{ currentSequence.status || 'pending' }}
													</span>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class="col-md-6">
									<div class="card bg-light">
										<div class="card-body py-2">
											<div class="row">
												<div class="col-6">
													<small class="text-muted d-block">Start Date</small>
													<strong class="text-primary">{{ formatCompactDate(currentSequence.sequence_start_at) }}</strong>
												</div>
												<div class="col-6">
													<small class="text-muted d-block">Total Items</small>
													<strong class="text-info">{{ currentSequenceItems.length }}</strong>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Loading State -->
						<div v-if="loadingSequenceItems" class="text-center py-4">
							<div class="spinner-border text-primary" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
							<p class="text-muted mt-2">Loading sequence items...</p>
						</div>

						<!-- Sequence Items Table -->
						<div v-else-if="currentSequenceItems.length > 0" class="table-responsive">
							<table class="table table-hover table-sm">
								<thead class="table-dark">
									<tr>
										<th>ID</th>
										<th>Recipient</th>
										<th>Status</th>
										<th>Scheduled</th>
										<th>Processed</th>
										<th>Created</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="item in currentSequenceItems" :key="item.id">
										<td>{{ item.id }}</td>
										<td>
											<div>
												<strong>{{ item.recipient_name || 'N/A' }}</strong>
												<br />
												<small class="text-muted">{{ item.recipient_email || item.recipient_address || 'N/A' }}</small>
											</div>
										</td>
										<td>
											<span class="badge badge-sm" :class="getItemStatusBadgeClass(item.status)">
												{{ item.status || 'pending' }}
											</span>
										</td>
										<td>
											<small>{{ formatCompactDate(item.scheduled_at) }}</small>
										</td>
										<td>
											<small>{{ item.processed_at ? formatCompactDate(item.processed_at) : '-' }}</small>
										</td>
										<td>
											<small>{{ formatCompactDate(item.created_at) }}</small>
										</td>
									</tr>
								</tbody>
							</table>
						</div>

						<!-- No Items -->
						<div v-else class="text-center py-4 text-muted">
							<i class="fad fa-inbox fa-3x mb-3"></i>
							<p>No items found for this sequence.</p>
						</div>
					</div>

					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
							<i class="fad fa-times me-1"></i>
							Close
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Test Sequence Modal -->
		<div class="modal fade" id="testSequenceModal" tabindex="-1" aria-labelledby="testSequenceModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="testSequenceModalLabel">Test Sequence Results</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div v-if="testSequenceLoading" class="text-center py-4">
							<i class="fad fa-spinner fa-spin fa-2x text-primary mb-3"></i>
							<p>Testing sequence...</p>
						</div>
						<div v-else-if="testSequenceResponse">
							<!-- Success Message -->
							<div v-if="testSequenceResponse.message" class="alert alert-success">
								<i class="fad fa-check-circle me-2"></i>
								{{ testSequenceResponse.message }}
							</div>

							<!-- Error Message -->
							<div v-else-if="testSequenceResponse.error" class="alert alert-danger">
								<i class="fad fa-exclamation-triangle me-2"></i>
								{{ testSequenceResponse.error }}
							</div>

							<!-- Test Item Details -->
							<div v-if="testSequenceResponse.data" class="card mb-3">
								<div class="card-header">
									<h6 class="mb-0">
										<i class="fad fa-info-circle me-2"></i>
										Test Item Details
									</h6>
								</div>
								<div class="card-body">
									<div class="row align-items-center mb-3">
										<div class="col">
											<strong>ID:</strong> {{ testSequenceResponse.data.id }}
										</div>
										<div class="col-auto" v-if="testSequenceResponse.data.url">
											<a :href="testSequenceResponse.data.url" target="_blank" class="btn btn-outline-primary btn-sm">
												<i class="fad fa-external-link me-1"></i>
												View Full Document
											</a>
										</div>
									</div>
									
									<div v-if="testSequenceResponse.data.thumbnail">
										<div class="mb-2">
											<strong>Preview:</strong>
										</div>
										<div v-if="!showThumbnail" class="d-flex align-items-center justify-content-center border rounded mx-auto" style="width: 100%; max-width: 400px; height: 300px;">
											<div class="text-center">
												<i class="fad fa-spinner fa-spin fa-3x text-primary mb-3"></i>
												<div><small class="text-muted">Loading preview...</small></div>
											</div>
										</div>
										<div v-else class="text-center">
											<img
												:src="testSequenceResponse.data.thumbnail"
												class="img-thumbnail"
												style="max-width: 100%; max-height: 400px;"
												alt="Test item thumbnail"
											/>
										</div>
									</div>
								</div>
							</div>

							<!-- Used Campaign Item -->
							<div v-if="testSequenceResponse.used_campaign_item" class="card mb-3">
								<div class="card-header">
									<h6 class="mb-0">
										<i class="fad fa-map-marker-alt me-2"></i>
										Test Recipient
									</h6>
								</div>
								<div class="card-body">
									<div class="row">
										<div class="col-md-12">
											<strong>{{ testSequenceResponse.used_campaign_item.name }}</strong
											><br />
											{{ testSequenceResponse.used_campaign_item.address }}<br />
											{{ testSequenceResponse.used_campaign_item.city }}, {{ testSequenceResponse.used_campaign_item.state }}
											{{ testSequenceResponse.used_campaign_item.zip }}
										</div>
									</div>
								</div>
							</div>

							<!-- Raw Response (Collapsible) -->
							<div class="card">
								<div class="card-header">
									<h6 class="mb-0">
										<button class="btn btn-link text-decoration-none p-0 text-start w-100" type="button" @click="showRawResponse = !showRawResponse">
											<i class="fad me-2" :class="showRawResponse ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
											Raw Response Data
										</button>
									</h6>
								</div>
								<div v-show="showRawResponse" class="card-body">
									<pre class="bg-light p-3 rounded small"><code>{{ JSON.stringify(testSequenceResponse, null, 2) }}</code></pre>
								</div>
							</div>
						</div>
						<div v-else class="text-center py-4 text-muted">
							<i class="fad fa-flask fa-3x mb-3"></i>
							<p>No test results available</p>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
							<i class="fad fa-times me-1"></i>
							Close
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import restAPI from '@/api/index.js'
import { Modal, Offcanvas } from 'bootstrap'
import { mapState } from 'vuex'
import UtmCampaignInput from '@/components/common/UtmCampaignInput'

export default {
	name: 'AutoCampaignWithSequencesEdit',
	components: {
		UtmCampaignInput,
	},
	data() {
		return {
			campaignId: null,
			loading: true,
			saving: false,
			campaign: {
				name: '',
				description: '',
				utm_campaign: '',
				status: 'draft',
				has_sequences: true,
				mode: 'test',
				mail_type: '',
				sequences: [],
				items_count: 0,
			},
			sequenceItems: [],
			newSequenceItem: {
				sequenced_campaign_id: '',
				name: '',
				description: '',
				type: 'postcard',
				postcard_front_url: '',
				postcard_front_html: '',
				postcard_back_url: '',
				postcard_back_html: '',
				postcard_size: '',
				letter_url: '',
				letter_html: '',
				letter_color: '',
				letter_double_sided: 0,
				letter_address_placement: '',
				letter_extra_service: '',
				email_html: '',
				email_template_id: null,
				email_provider_config_id: null,
				email_recipient_source: 'municipality_email_only',
				email_schedule_type: 'immediate',
				email_delay_value: 0,
				email_delay_unit: 'hours',
				email_depends_on_event: null,
				sender: '',
				mail_type: '',
				status: 'pending',
				chunk_size: 100,
				total_chunks: 0,
				chunk_time_gap: 0,
				sequence_time_gap: 0,
				sequence_start_at: '',
				cost: 0,
				position: 0,
			},
			selectedItemId: null,
			showAddDropdown: false,
			nextItemId: 1,
			senders: [],
			emailTemplates: [],
			emailProviderConfigs: [],
			campaignItems: [],
			loadingItems: false,
			// Error handling
			campaignErrors: [],
			sequenceErrors: {},
			showErrorPanel: false,
			hasCheckedStatus: false, // Track if check status has been completed
			itemsModal: null,
			issuesOffcanvas: null,
			startCampaignModal: null,
			isCalculatingSequences: false, // Flag to prevent recursive calculation calls
			startingCampaign: false,
			campaignStartStatus: '',
			sequenceItemsModal: null,
			currentSequence: null,
			currentSequenceItems: [],
			loadingSequenceItems: false,
			testSequenceModal: null,
			testSequenceLoading: false,
			testSequenceResponse: null,
			showRawResponse: false,
			showThumbnail: false,
		}
	},
	computed: {
		...mapState(['storageUrl']),
		totalErrorCount() {
			const campaignErrorCount = this.campaignErrors.length
			const sequenceErrorCount = Object.values(this.sequenceErrors).reduce((total, seq) => total + seq.errors.length, 0)
			return campaignErrorCount + sequenceErrorCount
		},
		hasErrors() {
			return this.totalErrorCount > 0
		},
		getCheckButtonClass() {
			if (this.hasErrors) {
				return 'btn-outline-danger'
			} else if (this.totalErrorCount === 0 && (this.campaignErrors.length > 0 || Object.keys(this.sequenceErrors).length > 0)) {
				// Previously checked and no errors
				return 'btn-outline-success'
			} else {
				// Initial state - blue
				return 'btn-outline-primary'
			}
		},
		getCheckButtonIcon() {
			if (this.hasErrors) {
				return 'fa-exclamation-triangle'
			} else if (this.totalErrorCount === 0 && (this.campaignErrors.length > 0 || Object.keys(this.sequenceErrors).length > 0)) {
				// Previously checked and no errors
				return 'fa-check-circle'
			} else {
				// Initial state - magnifying glass icon
				return 'fa-search'
			}
		},
		getTotalChunks() {
			return this.sequenceItems.reduce((total, item) => total + (parseInt(item.total_chunks) || 0), 0)
		},
		getTotalRecipients() {
			// Total recipients = number of sequences × campaign items count
			const campaignItemsCount = this.campaign.items_count || 0
			const sequencesCount = this.sequenceItems.length
			return sequencesCount * campaignItemsCount
		},
		isCampaignActive() {
			return this.campaign.status === 'active' || this.campaign.status === 'running' || this.campaign.status === 'finished'
		},
		hasStartedSequences() {
			return this.sequenceItems.some(item => item.status && item.status !== 'pending')
		},
	},
	watch: {
		// Watch for changes in sequence_start_at of first item or sequence_time_gap of any item
		sequenceItems: {
			handler(newItems, oldItems) {
				if (!newItems || newItems.length === 0) return
				if (!oldItems || oldItems.length === 0) return

				// Check if we need to recalculate by comparing relevant fields
				let shouldRecalculate = false

				// Check if first item's sequence_start_at changed
				const newFirstItem = newItems.find(item => item.position === 0)
				const oldFirstItem = oldItems.find(item => item.position === 0)

				if (newFirstItem && oldFirstItem && newFirstItem.sequence_start_at !== oldFirstItem.sequence_start_at) {
					shouldRecalculate = true
				}

				// Check if any item's sequence_time_gap changed
				if (!shouldRecalculate) {
					for (let i = 0; i < newItems.length; i++) {
						const newItem = newItems[i]
						const oldItem = oldItems.find(item => item.id === newItem.id)

						if (oldItem && newItem.sequence_time_gap !== oldItem.sequence_time_gap) {
							shouldRecalculate = true
							break
						}
					}
				}

				if (shouldRecalculate) {
					// Use nextTick to avoid infinite loops during calculation
					this.$nextTick(() => {
						this.calculateAllSequenceStartTimes()
					})
				}
			},
			deep: true,
		},
	},
	async created() {
		this.campaignId = this.$route.params.id
		if (this.campaignId) {
			await this.loadCampaign()
		}
		await this.loadSenders()
		await this.loadEmailTemplates()
		await this.loadEmailProviderConfigs()
	},
	methods: {
		async loadCampaign() {
			try {
				this.loading = true
				const response = await restAPI.get(`/sequenced-campaigns/${this.campaignId}`)

				// Load campaign data
				this.campaign = { ...this.campaign, ...response.data }

				// Load sequence items if they exist
				if (this.campaign.sequences && Array.isArray(this.campaign.sequences)) {
					this.sequenceItems = this.campaign.sequences
						.slice()
						.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
						.map(item => {
							const normalizedItem = {
								...item,
								id: item.id || this.nextItemId++,
								email_template_id: item.email_template_id || null,
								email_provider_config_id: item.email_provider_config_id || null,
								email_recipient_source: item.email_recipient_source || 'municipality_email_only',
								email_schedule_type: item.email_schedule_type || 'immediate',
								email_delay_value: item.email_delay_value || 0,
								email_delay_unit: item.email_delay_unit || 'hours',
								email_depends_on_event: item.email_depends_on_event || null,
							}
							// Normalize data types for form compatibility
							this.normalizeItemDataTypes(normalizedItem)
							return normalizedItem
						})
				}

				// Set next ID to avoid conflicts
				if (this.sequenceItems.length > 0) {
					this.nextItemId = Math.max(...this.sequenceItems.map(item => item.id)) + 1
				}

				// Update position attributes to ensure consistency
				await this.updateSequencePositions()

				// Calculate initial total chunks for each sequence item
				this.sequenceItems.forEach(item => {
					this.calculateTotalChunks(item)
				})

				// Calculate sequence start times after loading
				this.calculateAllSequenceStartTimes()
			} catch (error) {
				console.error('Error loading campaign:', error)
				this.$swal.fire({
					title: 'Error',
					text: 'Failed to load campaign data',
					icon: 'error',
				})
				// Redirect back to campaigns list on error
				this.$router.push({ name: 'autoCampaigns' })
			} finally {
				this.loading = false
			}
		},
		async loadSenders() {
			try {
				const response = await restAPI.get('/settings', {
					params: { name: 'LobSettings', type: 'settings' },
				})
				if (response.data && response.data.data) {
					const lobSettings = JSON.parse(response.data.data)
					this.senders = lobSettings.senders || []
				} else {
					this.senders = []
				}
			} catch (error) {
				console.error('Error loading senders:', error)
				// Fallback to empty senders array if API fails
				this.senders = []
			}
		},
		async loadEmailTemplates() {
			try {
				const response = await restAPI.get('/email-templates')
				this.emailTemplates = (response.data || []).filter(template => template.status !== 'archived')
			} catch (error) {
				console.error('Error loading email templates:', error)
				this.emailTemplates = []
			}
		},
		async loadEmailProviderConfigs() {
			try {
				const response = await restAPI.get('/email-provider-configs')
				this.emailProviderConfigs = (response.data || []).filter(config => config.status === 'active')
			} catch (error) {
				console.error('Error loading email provider configs:', error)
				this.emailProviderConfigs = []
			}
		},
		async checkCampaignReadiness() {
			try {
				// Clear previous errors
				this.campaignErrors = []
				this.sequenceErrors = {}
				this.showErrorPanel = false

				const response = await restAPI.get(`/sequenced-campaigns/${this.campaignId}/check-required-conditions`)

				if (response.data) {
					if (Array.isArray(response.data) && response.data.length > 0) {
						// Has errors - process them
						this.processCampaignErrors(response.data)
						this.showIssuesOffcanvas()
					}
					// Mark that we've completed a check
					this.hasCheckedStatus = true
				}
			} catch (error) {
				console.error('Error checking campaign readiness:', error)

				// Handle API errors
				const errorData = error.response ? error.response.data : null
				if (Array.isArray(errorData)) {
					// Structured error response in catch block
					this.processCampaignErrors(errorData)
					this.showIssuesOffcanvas()

					const totalErrors = errorData.reduce((total, seq) => total + seq.errors.length, 0)
					this.$swal.fire({
						icon: 'error',
						title: 'Validation Errors',
						text: `Found ${totalErrors} error(s). Check the error panel for details.`,
						showConfirmButton: true,
					})
					// Mark that we've completed a check (even with errors)
					this.hasCheckedStatus = true
				} else {
					// Generic error - don't set hasCheckedStatus since check failed
					const errorMessage = errorData ? errorData.message : null || error.message || 'Failed to check campaign readiness'
					this.$swal.fire({
						icon: 'error',
						title: 'Check Failed',
						text: errorMessage,
						showConfirmButton: true,
					})
				}
			}
		},
		processCampaignErrors(errorArray) {
			// Clear previous errors
			this.sequenceErrors = {}
			this.campaignErrors = []

			// Process each sequence error
			errorArray.forEach(sequenceError => {
				const sequenceId = sequenceError.sequence_id
				this.sequenceErrors[sequenceId] = {
					name: sequenceError.sequence_name,
					type: sequenceError.sequence_type,
					errors: sequenceError.errors.map(errorMsg => ({
						message: errorMsg,
						field: null,
					})),
				}
			})
		},
		async startCampaign() {
			// First check if campaign is ready
			if (this.hasErrors) {
				this.$swal.fire({
					icon: 'error',
					title: 'Cannot Start Campaign',
					text: 'Please fix all validation errors before starting the campaign.',
					showConfirmButton: true,
				})
				return
			}

			// Show start campaign modal instead of direct confirmation
			this.showStartCampaignModal()
		},
		async saveCampaign() {
			try {
				this.saving = true

				// Update campaign sequences before saving
				this.campaign.sequences = [...this.sequenceItems]

				const response = await restAPI.put(`/sequenced-campaigns/${this.campaignId}`, this.campaign)

				// Update local data with response
				this.campaign = { ...this.campaign, ...response.data }

				this.$swal.fire({
					title: 'Success',
					text: 'Campaign updated successfully!',
					icon: 'success',
					timer: 2000,
					showConfirmButton: false,
				})
			} catch (error) {
				console.error('Error saving campaign:', error)
				let errorMessage = 'Failed to save campaign changes'
				if (error.response && error.response.data && error.response.data.message) {
					errorMessage = error.response.data.message
				}
				this.$swal.fire({
					title: 'Error',
					text: errorMessage,
					icon: 'error',
				})
			} finally {
				this.saving = false
			}
		},
		calculateTotalChunks(item) {
			const chunkSize = parseInt(item.chunk_size) || 100
			const itemsCount = parseInt(this.campaign.items_count) || 0
			item.total_chunks = itemsCount > 0 ? Math.ceil(itemsCount / chunkSize) : 0
		},
		/**
		 * Calculate sequence start times for all sequence items based on the first item's start time and time gaps
		 */
		calculateAllSequenceStartTimes() {
			if (!this.sequenceItems || this.sequenceItems.length === 0) return
			if (this.isCalculatingSequences) return // Prevent recursive calls

			this.isCalculatingSequences = true

			try {
				// Sort items by position to ensure correct calculation order
				const sortedItems = [...this.sequenceItems].sort((a, b) => (a.position || 0) - (b.position || 0))

				// Find the first item (position 0)
				const firstItem = sortedItems.find(item => item.position === 0)
				if (!firstItem || !firstItem.sequence_start_at) {
					console.log('First sequence item or its start time is missing')
					return
				}

				let cumulativeStartTime = new Date(firstItem.sequence_start_at)

				// Calculate start times for subsequent items
				for (let i = 1; i < sortedItems.length; i++) {
					const currentItem = sortedItems[i]

					if (currentItem.sequence_time_gap && parseInt(currentItem.sequence_time_gap) > 0) {
						// Add the current item's time gap (in days) to the cumulative start time
						const timeGapDays = parseInt(currentItem.sequence_time_gap)
						cumulativeStartTime = new Date(cumulativeStartTime.getTime() + timeGapDays * 24 * 60 * 60 * 1000)
					}

					// Format the date to datetime-local format (YYYY-MM-DDTHH:MM)
					const formattedDateTime = this.formatDateTimeLocal(cumulativeStartTime)

					// Update the current item's sequence_start_at if it's different
					if (currentItem.sequence_start_at !== formattedDateTime) {
						currentItem.sequence_start_at = formattedDateTime

						// Auto-save the updated item to the database
						this.autoSaveSequenceItem(currentItem)
					}
				}
			} finally {
				this.isCalculatingSequences = false
			}
		},
		/**
		 * Format a Date object to datetime-local input format (YYYY-MM-DDTHH:MM)
		 */
		formatDateTimeLocal(date) {
			const year = date.getFullYear()
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const day = String(date.getDate()).padStart(2, '0')
			const hours = String(date.getHours()).padStart(2, '0')
			const minutes = String(date.getMinutes()).padStart(2, '0')

			return `${year}-${month}-${day}T${hours}:${minutes}`
		},
		/**
		 * Handle sequence time gap changes - triggers immediate recalculation
		 */
		onSequenceTimeGapChange() {
			this.$nextTick(() => {
				this.calculateAllSequenceStartTimes()
			})
		},
		/**
		 * Handle sequence start at changes for first item - triggers immediate recalculation
		 */
		onSequenceStartAtChange() {
			this.$nextTick(() => {
				this.calculateAllSequenceStartTimes()
			})
		},
		/**
		 * Normalize data types for form compatibility after server response
		 * Converts boolean values back to integers where needed
		 */
		normalizeItemDataTypes(item) {
			// Convert boolean values to integers for form compatibility
			if (typeof item.letter_double_sided === 'boolean') {
				item.letter_double_sided = item.letter_double_sided ? 1 : 0
			}
			if (typeof item.letter_color === 'boolean') {
				item.letter_color = item.letter_color ? 1 : 0
			}
			// Ensure sequence_time_gap is a string for select compatibility
			if (typeof item.sequence_time_gap === 'number') {
				item.sequence_time_gap = item.sequence_time_gap.toString()
			}
			// Convert null values to empty strings for form compatibility
			if (item.letter_extra_service === null || item.letter_extra_service === undefined) {
				item.letter_extra_service = ''
			}
			if (item.letter_address_placement === null || item.letter_address_placement === undefined) {
				item.letter_address_placement = ''
			}
			if (item.sender === null || item.sender === undefined) {
				item.sender = ''
			}
			// Add PDF preview properties if missing
			if (item.type === 'letter') {
				if (item.pdfLoading === undefined) {
					item.pdfLoading = false
				}
				if (!item.pdfViewMethod) {
					item.pdfViewMethod = 'iframe'
				}
				if (item.pdfExpanded === undefined) {
					item.pdfExpanded = false
				}
			}
		},
		/**
		 * Auto-save sequence item changes to the database without showing success messages
		 */
		async autoSaveSequenceItem(item) {
			try {
				if (!item.id) {
					console.warn('Cannot auto-save item without ID:', item)
					return
				}

				// Prepare item data for saving (same logic as saveSequenceItem but without UI feedback)
				const itemData = { ...item }
				delete itemData.postcard_front_file
				delete itemData.postcard_back_file
				delete itemData.campaign

				// Remove data URLs from item data as they can't be serialized
				if (itemData.postcard_front_url && itemData.postcard_front_url.startsWith('data:')) {
					delete itemData.postcard_front_url
				}
				if (itemData.postcard_back_url && itemData.postcard_back_url.startsWith('data:')) {
					delete itemData.postcard_back_url
				}

				// Send update to backend
				const response = await restAPI.put(`/sequenced-campaign-sequences/${item.id}`, itemData)

				// Update item with server response
				if (response.data) {
					// Preserve local data URLs that were excluded from the server request
					const preservedPostcardFrontUrl = item.postcard_front_url && item.postcard_front_url.startsWith('data:') ? item.postcard_front_url : null
					const preservedPostcardBackUrl = item.postcard_back_url && item.postcard_back_url.startsWith('data:') ? item.postcard_back_url : null

					Object.assign(item, response.data)

					// Restore preserved data URLs if they exist and server didn't provide alternatives
					if (preservedPostcardFrontUrl && !item.postcard_front_url) {
						item.postcard_front_url = preservedPostcardFrontUrl
					}
					if (preservedPostcardBackUrl && !item.postcard_back_url) {
						item.postcard_back_url = preservedPostcardBackUrl
					}

					// Normalize boolean/integer fields that should be integers for form compatibility
					this.normalizeItemDataTypes(item)
				}

				console.log(`Auto-saved sequence start time for item ${item.id}:`, item.sequence_start_at)
			} catch (error) {
				console.error('Error auto-saving sequence item:', error)
				// Don't show user-facing error for auto-save failures to avoid disrupting UX
			}
		},
		async saveSequenceItem(item) {
			try {
				// Since items are created in addSequenceItem, this method only handles updates
				if (!item.id) {
					throw new Error('Cannot update item without ID')
				}

				// Calculate and set cost per item before saving
				item.cost_per_item = this.calculateItemCost(item)

				// Check if we have files to upload
				const hasFiles = (item.type === 'postcard' && (item.postcard_front_file || item.postcard_back_file)) || (item.type === 'letter' && item.letter_pdf_file)

				if (hasFiles) {
					// Create FormData for multipart request
					const formData = new FormData()

					// Prepare item data without file objects and data URLs
					const itemData = { ...item }

					delete itemData.postcard_front_file
					delete itemData.postcard_back_file
					delete itemData.letter_pdf_file
					delete itemData.campaign

					// Remove data URLs from item data as they can't be serialized
					if (itemData.postcard_front_url && itemData.postcard_front_url.startsWith('data:')) {
						delete itemData.postcard_front_url
					}
					if (itemData.postcard_back_url && itemData.postcard_back_url.startsWith('data:')) {
						delete itemData.postcard_back_url
					}

					// Add each item field directly to FormData (only primitive values)
					Object.keys(itemData).forEach(key => {
						const value = itemData[key]
						// Only add primitive values, skip objects and arrays
						if (value !== null && value !== undefined && typeof value !== 'object') {
							formData.append(key, value)
						}
					})

					// Add files if they exist
					if (item.postcard_front_file) {
						formData.append('postcard_front_file', item.postcard_front_file)
					}
					if (item.postcard_back_file) {
						formData.append('postcard_back_file', item.postcard_back_file)
					}
					if (item.letter_pdf_file) {
						formData.append('letter_file', item.letter_pdf_file)
					}

					// Send multipart request
					const response = await restAPI.post(`/sequenced-campaign-sequences/${item.id}`, formData, {
						headers: {
							'Content-Type': 'multipart/form-data',
						},
					})

					// Update item with server response and clean up file references
					if (response.data) {
						Object.assign(item, response.data)
						// Normalize boolean/integer fields that should be integers for form compatibility
						this.normalizeItemDataTypes(item)
						delete item.postcard_front_file
						delete item.postcard_back_file
						delete item.letter_pdf_file
					}
				} else {
					// Regular JSON update for items without files
					const itemData = { ...item }
					delete itemData.postcard_front_file
					delete itemData.postcard_back_file
					delete itemData.letter_pdf_file

					const response = await restAPI.put(`/sequenced-campaign-sequences/${item.id}`, itemData)

					// Update item with server response
					if (response.data) {
						Object.assign(item, response.data)
						// Normalize boolean/integer fields that should be integers for form compatibility
						this.normalizeItemDataTypes(item)
					}
				}

				// Update the campaign sequences array
				this.campaign.sequences = [...this.sequenceItems]

				// Ensure the position is correctly preserved after save
				item.position = this.sequenceItems.findIndex(seqItem => seqItem.id === item.id)

				// Force Vue to update reactivity for disabled states
				this.$forceUpdate()

				// Close the configuration panel
				this.selectedItemId = null

				this.$swal.fire({
					icon: 'success',
					title: 'Sequence item updated successfully!',
					timer: 2000,
					showConfirmButton: false,
				})
			} catch (error) {
				console.error('Error updating sequence item:', error)

				let errorMessage = 'Failed to update sequence item'
				if (error.response ? error.response.data : null?.message) {
					errorMessage = error.response.data.message
				} else if (error.response ? error.response.data : null?.error) {
					// Handle validation errors
					const errors = error.response.data.error
					if (typeof errors === 'object') {
						errorMessage = Object.values(errors).flat().join(', ')
					} else {
						errorMessage = errors
					}
				}

				this.$swal.fire({
					icon: 'error',
					title: 'Error',
					text: errorMessage,
				})
			}
		},
		toggleAddDropdown() {
			this.showAddDropdown = !this.showAddDropdown
		},
		async addSequenceItem(type) {
			try {
				const newItem = {
					...this.newSequenceItem,
					type: type,
					sequenced_campaign_id: this.campaignId,
					name: `${type.charAt(0).toUpperCase() + type.slice(1)} ${this.sequenceItems.length + 1}`,
					position: this.sequenceItems.length,
				}

				// Set type-specific defaults
				if (type === 'email') {
					newItem.postcard_front_url = ''
					newItem.postcard_back_url = ''
					newItem.postcard_size = ''
					newItem.letter_url = ''
					newItem.letter_html = ''
					newItem.letter_color = ''
					newItem.letter_double_sided = 0
					newItem.letter_address_placement = ''
					newItem.letter_extra_service = ''
					newItem.email_template_id = null
					newItem.email_provider_config_id = null
					newItem.email_recipient_source = 'municipality_email_only'
					newItem.email_schedule_type = 'immediate'
					newItem.email_delay_value = 0
					newItem.email_delay_unit = 'hours'
				} else if (type === 'postcard') {
					newItem.postcard_size = '4x6'
					newItem.email_html = ''
					newItem.letter_url = ''
					newItem.letter_html = ''
					newItem.letter_color = ''
					newItem.letter_double_sided = 0
					newItem.letter_address_placement = ''
					newItem.letter_extra_service = ''
				} else if (type === 'letter') {
					newItem.letter_color = '0'
					newItem.letter_double_sided = 0
					newItem.letter_address_placement = 'top_first_page'
					newItem.postcard_front_url = ''
					newItem.postcard_back_url = ''
					newItem.postcard_size = ''
					newItem.email_html = ''
					// PDF preview properties
					newItem.pdfLoading = false
					newItem.pdfViewMethod = 'iframe'
					newItem.pdfExpanded = false
				}

				// Calculate initial total chunks for the new item
				this.calculateTotalChunks(newItem)

				console.log('New item:', newItem)

				// Create item in backend first
				const response = await restAPI.post('/sequenced-campaign-sequences', newItem)

				// Update the item with the server response data
				if (response.data) {
					// If response.data is the actual item, use it directly
					Object.assign(newItem, response.data)
					// Normalize boolean/integer fields that should be integers for form compatibility
					this.normalizeItemDataTypes(newItem)
				}

				this.sequenceItems.push(newItem)
				// Update position attributes for all items
				await this.updateSequencePositions()

				// Calculate sequence start times for all items
				this.calculateAllSequenceStartTimes()

				// Store in campaign sequences array
				this.campaign.sequences = [...this.sequenceItems]
				this.selectItem(newItem.id)
				this.showAddDropdown = false

				this.$swal.fire({
					icon: 'success',
					title: 'Sequence item created successfully!',
					timer: 2000,
					showConfirmButton: false,
				})
			} catch (error) {
				console.error('Error creating sequence item:', error)

				let errorMessage = 'Failed to create sequence item'
				if (error.response ? error.response.data : null?.message) {
					errorMessage = error.response.data.message
				} else if (error.response ? error.response.data : null?.error) {
					// Handle validation errors
					const errors = error.response.data.error
					if (typeof errors === 'object') {
						errorMessage = Object.values(errors).flat().join(', ')
					} else {
						errorMessage = errors
					}
				}

				this.$swal.fire({
					icon: 'error',
					title: 'Error',
					text: errorMessage,
				})

				this.showAddDropdown = false
			}
		},
		selectItem(itemId) {
			if (this.selectedItemId === itemId) {
				// Toggle off if clicking the same item
				this.selectedItemId = null
			} else {
				// Select new item
				this.selectedItemId = itemId
			}
		},
		selectCampaignInfo() {
			if (this.selectedItemId === 'campaign') {
				// Toggle off if clicking the same campaign
				this.selectedItemId = null
			} else {
				// Select campaign
				this.selectedItemId = 'campaign'
			}
		},
		async removeItem(index) {
			const removedItem = this.sequenceItems[index]

			try {
				// If the item has an ID, delete it from the backend
				if (removedItem.id) {
					await restAPI.delete(`/sequenced-campaign-sequences/${removedItem.id}`)
				}

				// Remove from local array
				this.sequenceItems.splice(index, 1)

				// Update position attributes after removal
				await this.updateSequencePositions()

				// Recalculate sequence start times after removal
				this.calculateAllSequenceStartTimes()

				// Update the campaign sequences array
				this.campaign.sequences = [...this.sequenceItems]

				// If the removed item was selected, clear selection or select another item
				if (this.selectedItemId === removedItem.id) {
					if (this.sequenceItems.length > 0) {
						// Select the item at the same index, or the last item if index is out of bounds
						const newIndex = Math.min(index, this.sequenceItems.length - 1)
						const newSelectedItem = this.sequenceItems[newIndex]
						this.selectedItemId = newSelectedItem ? newSelectedItem.id : null
					} else {
						this.selectedItemId = null
					}
				}

				this.$swal.fire({
					icon: 'success',
					title: 'Sequence item removed successfully!',
					timer: 2000,
					showConfirmButton: false,
				})
			} catch (error) {
				console.error('Error removing sequence item:', error)

				let errorMessage = 'Failed to remove sequence item'
				if (error.response ? error.response.data : null?.message) {
					errorMessage = error.response.data.message
				}

				this.$swal.fire({
					icon: 'error',
					title: 'Error',
					text: errorMessage,
				})
			}
		},
		async moveItem(index, direction) {
			const newIndex = index + direction
			if (newIndex < 0 || newIndex >= this.sequenceItems.length) return

			// Swap items using Vue.js array methods for reactivity
			const items = [...this.sequenceItems]
			const temp = items[index]
			items[index] = items[newIndex]
			items[newIndex] = temp
			this.sequenceItems = items

			// Update position attributes for all items
			await this.updateSequencePositions()

			// Recalculate sequence start times after move
			this.calculateAllSequenceStartTimes()
		},
		async updateSequencePositions() {
			try {
				// Create array of items with id and position for backend
				const itemsWithPositions = this.sequenceItems
					.filter(item => item.id) // Only include items that exist in backend
					.map((item, index) => ({
						id: item.id,
						position: index,
					}))

				// Update local positions first
				this.sequenceItems.forEach((item, index) => {
					item.position = index
				})

				// If we have items to update in backend
				if (itemsWithPositions.length > 0) {
					// Send position updates to backend
					await restAPI.post(`/sequenced-campaign-sequences/${this.campaignId}/order`, {
						items: itemsWithPositions,
					})
				}
			} catch (error) {
				console.error('Error updating sequence positions:', error)

				// Show error message but don't revert local changes
				// as the user experience should continue working
				this.$swal.fire({
					icon: 'warning',
					title: 'Position Update Warning',
					text: 'Failed to sync position changes with server. Changes are saved locally.',
					timer: 3000,
					showConfirmButton: false,
				})
			}
		},
		getItemTypeClass(type) {
			const classes = {
				email: 'email-node',
				postcard: 'postcard-node',
				letter: 'letter-node',
			}
			return classes[type] || ''
		},
		getItemTypeLabel(type) {
			const labels = {
				email: 'Email',
				postcard: 'Postcard',
				letter: 'Letter',
			}
			return labels[type] || type
		},
		getStatusClass(status) {
			const statusClasses = {
				active: 'text-success',
				completed: 'text-primary',
				failed: 'text-danger',
				pending: 'text-warning',
				paused: 'text-secondary',
				draft: 'text-muted',
			}
			return statusClasses[status] || 'text-muted'
		},
		getStatusBadgeClass(status) {
			const badgeClasses = {
				draft: 'bg-secondary',
				active: 'bg-success',
				paused: 'bg-secondary',
				completed: 'bg-primary',
			}
			return badgeClasses[status] || 'bg-secondary'
		},
		getStatusBadgeOutlineClass(status) {
			const outlineClasses = {
				draft: 'badge-outline-orange',
				active: 'badge-outline-green',
				paused: 'badge-outline-gray',
				completed: 'badge-outline-blue',
			}
			return outlineClasses[status] || 'badge-outline-orange'
		},
		getModeBadgeClass(mode) {
			const badgeClasses = {
				test: 'bg-warning',
				live: 'bg-success',
			}
			return badgeClasses[mode] || 'bg-secondary'
		},
		getCampaignModeClass(mode) {
			const modeClasses = {
				test: 'campaign-mode-test',
				live: 'campaign-mode-live',
			}
			return modeClasses[mode] || 'campaign-mode-test'
		},
		getCampaignModeIcon(mode) {
			const modeIcons = {
				test: 'fa-flask',
				live: 'fa-rocket',
			}
			return modeIcons[mode] || 'fa-flask'
		},
		getCampaignModeLabel(mode) {
			const modeLabels = {
				test: 'TEST MODE',
				live: 'LIVE MODE',
			}
			return modeLabels[mode] || 'TEST MODE'
		},
		triggerPostcardUpload(item, side) {
			const input = document.createElement('input')
			input.type = 'file'
			input.accept = 'image/*'
			input.onchange = e => this.handlePostcardUpload(item, side, e)
			input.click()
		},
		handlePostcardUpload(item, side, event) {
			const file = event.target.files[0]
			if (file) {
				// Store the actual file for upload using Vue.set for reactivity
				if (side === 'front') {
					this.$set(item, 'postcard_front_file', file)
				} else {
					this.$set(item, 'postcard_back_file', file)
				}

				// Create FileReader to show preview as data URL
				const reader = new FileReader()
				reader.onload = e => {
					if (side === 'front') {
						this.$set(item, 'postcard_front_url', e.target.result) // data:image/... URL
					} else {
						this.$set(item, 'postcard_back_url', e.target.result) // data:image/... URL
					}
					// Force reactivity update
					this.$forceUpdate()
				}
				reader.readAsDataURL(file)
			}
		},
		removePostcardImage(item, side) {
			if (side === 'front') {
				item.postcard_front_url = null
				delete item.postcard_front_file
			} else {
				item.postcard_back_url = null
				delete item.postcard_back_file
			}
			this.campaign.sequences = [...this.sequenceItems]
		},
		triggerLetterUpload(item) {
			const input = document.createElement('input')
			input.type = 'file'
			input.accept = 'application/pdf'
			input.onchange = e => this.handleLetterUpload(item, e)
			input.click()
		},
		handleLetterUpload(item, event) {
			const file = event.target.files[0]
			if (file) {
				// Store the actual file for upload
				this.$set(item, 'letter_pdf_file', file)
				this.$set(item, 'letter_pdf_name', file.name)

				// Set the letter_url to indicate a file is present
				this.$set(item, 'letter_url', 'uploaded')

				// Set PDF preview properties
				this.$set(item, 'pdfLoading', true)
				this.$set(item, 'pdfViewMethod', 'iframe')

				// Create FileReader to generate data URL for preview
				const reader = new FileReader()
				reader.onload = e => {
					this.$set(item, 'letter_pdf_data', e.target.result)
					// Give a small delay to show loading state
					setTimeout(() => {
						this.$set(item, 'pdfLoading', false)
					}, 500)
				}
				reader.readAsDataURL(file)

				// Force reactivity update
				this.$forceUpdate()
			}
		},
		removeLetterPdf(item) {
			item.letter_url = null
			item.letter_pdf_name = null
			delete item.letter_pdf_file
			delete item.letter_pdf_data
			this.campaign.sequences = [...this.sequenceItems]
		},
		getLetterPdfUrl(item) {
			// Return the data URL for uploaded files, or server URL for existing files
			if (item.letter_pdf_data) {
				return item.letter_pdf_data
			}
			if (item.letter_url && item.letter_url !== 'uploaded') {
				return this.storageUrl + item.letter_url
			}
			return ''
		},
		onPdfLoad(item) {
			this.$set(item, 'pdfLoading', false)
		},
		onPdfError(item) {
			// If iframe fails, try object/embed
			if (item.pdfViewMethod === 'iframe') {
				this.$set(item, 'pdfViewMethod', 'object')
			} else if (item.pdfViewMethod === 'object') {
				// If object/embed also fails, show fallback
				this.$set(item, 'pdfViewMethod', 'fallback')
			}
		},
		togglePdfPreview(item) {
			this.$set(item, 'pdfExpanded', !item.pdfExpanded)
		},
		hasSequenceErrors(item) {
			return this.sequenceErrors[item.id] && this.sequenceErrors[item.id].errors.length > 0
		},
		showIssuesOffcanvas() {
			// Initialize offcanvas if not already initialized
			if (!this.issuesOffcanvas) {
				this.issuesOffcanvas = new Offcanvas(document.getElementById('campaignIssuesOffcanvas'))
			}
			this.issuesOffcanvas.show()
		},
		async showItemsModal() {
			// Initialize and show modal
			if (!this.itemsModal) {
				this.itemsModal = new Modal(document.getElementById('itemsModal'))
			}
			this.itemsModal.show()

			// Load items if not already loaded
			if (this.campaignItems.length === 0) {
				await this.loadCampaignItems()
			}
		},
		async loadCampaignItems() {
			this.campaignItems = this.campaign.items
		},
		getItemStatusClass(status) {
			const statusClasses = {
				active: 'badge-outline-primary',
				running: 'badge-outline-primary',
				completed: 'badge-outline-success',
				sent: 'badge-outline-success',
				delivered: 'badge-outline-success',
				failed: 'badge-outline-danger',
				pending: 'badge-outline-warning',
				scheduled: 'badge-outline-info',
				processing: 'badge-outline-primary',
				paused: 'badge-outline-secondary',
				cancelled: 'badge-outline-secondary',
			}
			return statusClasses[status] || 'badge-outline-secondary'
		},
		formatDate(dateString) {
			if (!dateString) return '-'
			return new Date(dateString).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			})
		},
		formatNumber(num) {
			if (!num) return '0'
			return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
		},
		getPostcardImageUrl(imageUrl) {
			if (!imageUrl) return null

			// If it's a data URL (local preview), return as is
			if (imageUrl.startsWith('data:')) {
				return imageUrl
			}

			// If it's already a full URL, return as is
			if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
				return imageUrl
			}

			// Otherwise, prepend storageUrl
			return this.storageUrl + imageUrl
		},
		// Bottom Indicator Methods
		getSequenceStatusClass(item) {
			const status = item.status || 'pending'
			return {
				'status-pending': status === 'pending',
				'status-running': status === 'running',
				'status-completed': status === 'completed',
			}
		},
		getSequenceTimeDisplay(item) {
			if (!item.sequence_start_at) {
				return 'Start time missing'
			}

			// Format the datetime-local to a readable format
			const parts = item.sequence_start_at.split('T')
			const datePart = parts[0]
			const timePart = parts[1] || '00:00'

			// Convert date to more readable format
			const [year, month, day] = datePart.split('-')
			const date = new Date(year, month - 1, day)
			const dateFormatted = date.toLocaleDateString('en-US', {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			})

			// Format time to 12-hour format
			const [hours, minutes] = timePart.split(':').map(Number)
			const time12Hour = new Date(2000, 0, 1, hours, minutes).toLocaleTimeString('en-US', {
				hour: 'numeric',
				minute: '2-digit',
				hour12: true,
			})

			return `${dateFormatted} at ${time12Hour}`
		},
		getSequenceTimeClass(item) {
			return {
				'time-missing': !item.sequence_start_at,
				'time-set': !!item.sequence_start_at,
			}
		},
		// Start Campaign Modal Methods
		showStartCampaignModal() {
			// Initialize and show modal
			if (!this.startCampaignModal) {
				this.startCampaignModal = new Modal(document.getElementById('startCampaignModal'))
			}
			this.startCampaignModal.show()
		},
		async confirmStartCampaign() {
			// Set loading state
			this.startingCampaign = true

			// Status messages sequence
			const statusMessages = ['Creating campaign...', 'Preparing sequences...', 'Creating sequence items...', 'Finalizing things...']

			let currentStep = 0
			let statusInterval

			// Function to cycle through status messages
			const updateStatus = () => {
				this.campaignStartStatus = statusMessages[currentStep]
				currentStep = (currentStep + 1) % statusMessages.length // Cycle back to 0 after reaching end
			}

			// Start cycling through status messages every 1 second
			updateStatus() // Show first message immediately
			statusInterval = setInterval(updateStatus, 1000)

			try {
				// Make the API call to start campaign (this will run in parallel with status messages)
				const response = await restAPI.post(`/sequenced-campaigns/${this.campaignId}/start`)
				console.log('Campaign started:', response.data)

				// Stop the status cycling
				clearInterval(statusInterval)

				// Reset loading state
				this.startingCampaign = false
				this.campaignStartStatus = ''

				// Close modal after completion
				if (this.startCampaignModal) {
					this.startCampaignModal.hide()
				}

				// Reload campaign data to reflect new status
				this.loadCampaign()
			} catch (error) {
				console.error('Error starting campaign:', error)

				// Stop the status cycling
				clearInterval(statusInterval)

				// Reset loading state
				this.startingCampaign = false
				this.campaignStartStatus = ''
			}
		},
		formatScheduleDate(dateString) {
			if (!dateString) return 'Not scheduled'

			try {
				// Handle datetime-local format (YYYY-MM-DDTHH:MM)
				const date = new Date(dateString)

				return date.toLocaleDateString('en-US', {
					year: 'numeric',
					month: 'short',
					day: 'numeric',
					hour: '2-digit',
					minute: '2-digit',
					hour12: true,
				})
			} catch (error) {
				return 'Invalid date'
			}
		},
		getSequenceTypeIcon(type) {
			const icons = {
				postcard: 'fa-image',
				letter: 'fa-envelope',
				email: 'fa-at',
			}
			return icons[type] || 'fa-mail-bulk'
		},
		getRelativeTime(dateString) {
			if (!dateString) return ''

			try {
				const date = new Date(dateString)
				const now = new Date()

				// Compare dates by setting time to midnight for accurate day comparison
				const dateAtMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate())
				const nowAtMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate())

				const diffMs = dateAtMidnight - nowAtMidnight
				const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24))

				if (diffDays < 0) {
					return `${Math.abs(diffDays)} days ago`
				} else if (diffDays === 0) {
					return 'Today'
				} else if (diffDays === 1) {
					return 'Tomorrow'
				} else {
					return `In ${diffDays} days`
				}
			} catch (error) {
				return ''
			}
		},
		formatTimeGap(minutes) {
			if (!minutes || minutes === 0) return 'No gap'

			if (minutes < 60) {
				return `${minutes}m`
			} else if (minutes < 1440) {
				// Less than 24 hours
				const hours = Math.floor(minutes / 60)
				const remainingMinutes = minutes % 60
				if (remainingMinutes === 0) {
					return `${hours}h`
				} else {
					return `${hours}h ${remainingMinutes}m`
				}
			} else {
				// 24+ hours
				const days = Math.floor(minutes / 1440)
				const remainingHours = Math.floor((minutes % 1440) / 60)
				if (remainingHours === 0) {
					return `${days}d`
				} else {
					return `${days}d ${remainingHours}h`
				}
			}
		},
		formatCompactDate(dateString) {
			if (!dateString) return 'Not set'

			try {
				const date = new Date(dateString)

				return date.toLocaleDateString('en-US', {
					month: 'short',
					day: 'numeric',
					hour: '2-digit',
					minute: '2-digit',
					hour12: true,
				})
			} catch (error) {
				return 'Invalid'
			}
		},
		getLastSequenceEndDate() {
			if (!this.sequenceItems || this.sequenceItems.length === 0) return null

			// Find the last sequence item (highest position/latest start time)
			let lastSequenceItem = null
			let latestStartTime = null

			this.sequenceItems.forEach(item => {
				if (item.sequence_start_at) {
					const startTime = new Date(item.sequence_start_at)
					if (!latestStartTime || startTime > latestStartTime) {
						latestStartTime = startTime
						lastSequenceItem = item
					}
				}
			})

			if (!lastSequenceItem) return null

			// Calculate end date: last sequence start + (number of chunks * chunk gap)
			try {
				const startDate = new Date(lastSequenceItem.sequence_start_at)
				const totalChunks = lastSequenceItem.total_chunks || 1
				const chunkTimeGap = lastSequenceItem.chunk_time_gap || 0

				// Calculate total time in minutes: (chunks - 1) * gap between chunks
				// First chunk starts immediately, then gaps between subsequent chunks
				const totalTimeInMinutes = (totalChunks - 1) * chunkTimeGap

				// Add the chunk time + 2 hours (120 minutes) to start date
				const totalTimeWithBuffer = totalTimeInMinutes + 120 // Add 2 hours
				const endDate = new Date(startDate.getTime() + totalTimeWithBuffer * 60 * 1000)

				return endDate.toISOString().slice(0, 16) // Format as datetime-local without timezone conversion
			} catch (error) {
				return null
			}
		},
		formatServerDate(dateString) {
			if (!dateString) return '-'
			try {
				// Parse the server datetime without timezone conversion
				// Format: 2024-01-15T14:30:00 -> Jan 15, 2:30 PM
				const date = dateString.replace('T', ' ').substring(0, 16) // Remove seconds and timezone
				const [datePart, timePart] = date.split(' ')
				const [, month, day] = datePart.split('-')
				const [hour, minute] = timePart.split(':')

				const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
				const monthName = monthNames[parseInt(month) - 1]

				// Convert to 12-hour format
				let displayHour = parseInt(hour)
				const ampm = displayHour >= 12 ? 'PM' : 'AM'
				displayHour = displayHour % 12
				displayHour = displayHour ? displayHour : 12 // 0 should be 12

				return `${monthName} ${parseInt(day)}, ${displayHour}:${minute} ${ampm}`
			} catch (error) {
				return dateString // Return original if parsing fails
			}
		},
		formatCompactTimeGap(minutes) {
			if (!minutes || minutes === 0) return 'None'

			if (minutes < 60) {
				return `${minutes}m`
			} else if (minutes < 1440) {
				const hours = Math.floor(minutes / 60)
				return `${hours}h`
			} else {
				const days = Math.floor(minutes / 1440)
				return `${days}d`
			}
		},
		calculateEndDate(item) {
			if (!item.sequence_start_at || !item.total_chunks || !item.chunk_time_gap) {
				return null
			}

			try {
				const startDate = new Date(item.sequence_start_at)
				const totalChunks = item.total_chunks || 1
				const chunkTimeGap = item.chunk_time_gap || 0

				// Calculate total time in minutes for all chunks except the first
				// (first chunk starts immediately, then we have gaps between chunks)
				const totalTimeInMinutes = (totalChunks - 1) * chunkTimeGap

				// Add the time to start date
				const endDate = new Date(startDate.getTime() + totalTimeInMinutes * 60 * 1000)

				return endDate.toISOString().slice(0, 16) // Format as datetime-local
			} catch (error) {
				return null
			}
		},
		// Sequence Items Inline Methods
		async selectSequenceForItems(sequence) {
			// If already selected, toggle off
			if (this.selectedItemId === sequence.id) {
				this.selectedItemId = null
				this.currentSequence = null
				this.currentSequenceItems = []
				return
			}

			// Select this sequence and load its items
			this.selectedItemId = sequence.id
			this.currentSequence = sequence
			this.currentSequenceItems = []
			this.loadingSequenceItems = true

			// Load sequence items
			await this.loadSequenceItems(sequence.id)
		},
		async loadSequenceItems(sequenceId) {
			try {
				const response = await restAPI.get(`/sequenced-campaign-sequence-items/sequence-items/${sequenceId}`)
				this.currentSequenceItems = response.data || []
			} catch (error) {
				console.error('Error loading sequence items:', error)
				this.currentSequenceItems = []
			} finally {
				this.loadingSequenceItems = false
			}
		},
		getSequenceStatusBadgeClass(status) {
			const statusClasses = {
				pending: 'badge-outline-warning',
				running: 'badge-outline-primary',
				sending: 'badge-outline-primary badge-sending-pulse',
				completed: 'badge-outline-success',
				paused: 'badge-outline-secondary',
				failed: 'badge-outline-danger',
			}
			return statusClasses[status] || 'badge-outline-secondary'
		},
		getItemStatusBadgeClass(status) {
			const statusClasses = {
				pending: 'badge-outline-warning',
				scheduled: 'badge-outline-info',
				processing: 'badge-outline-primary',
				running: 'badge-outline-primary',
				sending: 'badge-outline-primary badge-sending-pulse',
				sent: 'badge-outline-success',
				delivered: 'badge-outline-success',
				completed: 'badge-outline-success',
				failed: 'badge-outline-danger',
				error: 'badge-outline-danger',
				cancelled: 'badge-outline-secondary',
				paused: 'badge-outline-secondary',
			}
			return statusClasses[status] || 'badge-outline-secondary'
		},
		canCancelItem(item) {
			// Allow cancellation for items that are not already cancelled, failed, or delivered
			const cancellableStatuses = ['pending', 'scheduled', 'processing', 'sent', 'completed']

			// If item doesn't have cancellable status (unless already cancelling), return false
			if (!cancellableStatuses.includes(item.status) && !item.cancelling) {
				return false
			}

			// If already in cancelling state, show the button
			if (item.cancelling) {
				return true
			}

			// Time-based logic: Cancel button should only be visible if current time is less than
			// sent_at + 20 minutes
			try {
				if (!item.sent_at) {
					// If no sent_at time, allow cancellation based on status only
					return true
				}

				const currentTime = new Date()
				const sentTime = new Date(item.sent_at)

				// Calculate cutoff time: sent_at + 20 minutes
				const cutoffTime = new Date(sentTime.getTime() + 20 * 60 * 1000) // Add 20 minutes

				// Allow cancellation if current time is before the cutoff time
				return currentTime < cutoffTime
			} catch (error) {
				console.error('Error calculating cancel window for item:', error)
				// If time calculation fails, fall back to status-based logic
				return true
			}
		},
		canCancelSequence(sequence) {
			// Allow sequence cancellation for sending or completed sequences
			const cancellableStatuses = ['sending', 'completed', 'active']

			// If sequence doesn't have cancellable status (unless already cancelling), return false
			if (!cancellableStatuses.includes(sequence.status) && !sequence.cancelling) {
				return false
			}

			// If already in cancelling state, show the button
			if (sequence.cancelling) {
				return true
			}

			// Time-based logic: Cancel button should only be visible if current time is less than
			// sequence_start_at + (chunk_gap_time * number_of_chunks) + 20 minutes
			try {
				if (!sequence.sequence_start_at || !sequence.total_chunks || !sequence.chunk_time_gap) {
					// If missing required data for time calculation, allow cancellation based on status only
					return true
				}

				const currentTime = new Date()
				const startDate = new Date(sequence.sequence_start_at)
				const totalChunks = sequence.total_chunks || 1
				const chunkTimeGap = sequence.chunk_time_gap || 0

				// Calculate end time: start + (chunk_gap * chunks) + 20 minutes buffer
				const totalTimeInMinutes = chunkTimeGap * totalChunks + 20 // Add 20 minute buffer
				const endTimeWithBuffer = new Date(startDate.getTime() + totalTimeInMinutes * 60 * 1000)

				// Allow cancellation if current time is before the end time + buffer
				return currentTime < endTimeWithBuffer
			} catch (error) {
				console.error('Error calculating cancel window:', error)
				// If time calculation fails, fall back to status-based logic
				return true
			}
		},
		cancelSequenceItem(item) {
			console.log('cancelSequenceItem called with item:', item)

			this.$swal
				.fire({
					title: 'Are you sure?',
					text: `Cancel sequence item for ${item.name || 'N/A'}? This action cannot be undone!`,
					icon: 'warning',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Confirm',
					cancelButtonText: 'Cancel',
					toast: false,
					position: 'center',
					backdrop: true,
					allowOutsideClick: true,
					allowEscapeKey: true,
					timer: undefined,
					timerProgressBar: false,
				})
				.then(result => {
					if (result.isConfirmed) {
						// Set cancelling state to show loading spinner
						this.$set(item, 'cancelling', true)

						// Call the cancellation API
						restAPI
							.post('/sequenced-campaign-lob/cancel-sequence-item', {
								sequence_item_id: item.id,
							})
							.then(response => {
								if (response.data && response.data.success) {
									// Update item status
									item.status = response.data.status || 'cancelled'
									this.$set(item, 'cancelling', false)

									// Show success message
									this.$swal.fire({
										title: 'Cancelled!',
										text: response.data.message || 'Sequence item has been cancelled.',
										icon: 'success',
										confirmButtonText: 'OK',
									})

									// Refresh sequence items to get updated data
									this.loadSequenceItems(this.currentSequence.id)
								}
							})
							.catch(error => {
								this.$set(item, 'cancelling', false)
								console.error('Error cancelling sequence item:', error)

								let errorMessage = 'Something went wrong while cancelling the sequence item!'
								let footer = ''

								if (error.response && error.response.data) {
									const errorData = error.response.data
									if (errorData.message) {
										errorMessage = errorData.message
									} else if (errorData.error && errorData.reason) {
										errorMessage = `${errorData.error}: ${errorData.reason}`
									} else if (errorData.error) {
										errorMessage = errorData.error
									}

									if (error.response.status === 404) {
										footer = '<a href="#">Item not found or already processed</a>'
									} else if (error.response.status >= 500) {
										footer = '<a href="#">Server error - please try again later</a>'
									}
								}

								this.$swal.fire({
									icon: 'error',
									title: 'Oops...',
									text: errorMessage,
									footer: footer,
								})
							})
					}
				})
		},
		cancelFullSequence(sequence) {
			console.log('cancelFullSequence called with sequence:', sequence)

			this.$swal
				.fire({
					title: 'Cancel Entire Sequence?',
					text: `Cancel all items in sequence "${sequence.name}"? This will attempt to cancel all items that can be cancelled.`,
					icon: 'warning',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Confirm',
					cancelButtonText: 'Cancel',
					toast: false,
					position: 'center',
					backdrop: true,
					allowOutsideClick: true,
					allowEscapeKey: true,
					timer: undefined,
					timerProgressBar: false,
				})
				.then(result => {
					if (result.isConfirmed) {
						// Set cancelling state to show loading spinner
						this.$set(sequence, 'cancelling', true)

						// Call the sequence cancellation API
						restAPI
							.post('/sequenced-campaign-lob/cancel-sequence', {
								sequence_id: sequence.id,
							})
							.then(response => {
								this.$set(sequence, 'cancelling', false)

								if (response.data) {
									// Update sequence status regardless of success/failure
									sequence.status = response.data.status
									if (response.data.cancelled_at) {
										sequence.cancelled_at = response.data.cancelled_at
									}

									// Handle both success and no-items-cancelled responses
									if (response.data.success) {
										// Success - some items were cancelled
										const summary = response.data.summary || {}
										const summaryText = `Successfully cancelled ${summary.cancelled || 0} items. ${summary.errors || 0} errors, ${
											summary.skipped || 0
										} skipped.`

										this.$swal.fire({
											title: 'Sequence Cancelled!',
											text: response.data.message || summaryText,
											icon: 'success',
											confirmButtonText: 'OK',
										})
									} else {
										// No items were cancelled - show warning
										const summary = response.data.summary || {}
										const summaryText = `No items cancelled. ${summary.errors || 0} errors, ${summary.skipped || 0} skipped.`

										this.$swal.fire({
											title: 'Cancellation Failed',
											text: response.data.message || summaryText,
											icon: 'warning',
											confirmButtonText: 'OK',
										})
									}

									// Refresh sequence items to get updated data
									this.loadSequenceItems(sequence)
								}
							})
							.catch(error => {
								this.$set(sequence, 'cancelling', false)
								console.error('Error cancelling sequence:', error)

								let errorMessage = 'Something went wrong while cancelling the sequence!'
								let footer = ''

								if (error.response && error.response.data) {
									const errorData = error.response.data
									if (errorData.reason) {
										errorMessage = `${errorData.error}: ${errorData.reason}`
									} else if (errorData.error) {
										errorMessage = errorData.error
									}

									if (error.response.status === 404) {
										footer = '<a href="#">Sequence not found</a>'
									} else if (error.response.status === 400) {
										footer = '<a href="#">Sequence cannot be cancelled in current status</a>'
									} else if (error.response.status >= 500) {
										footer = '<a href="#">Server error - please try again later</a>'
									}
								}

								this.$swal.fire({
									icon: 'error',
									title: 'Oops...',
									text: errorMessage,
									footer: footer,
								})
							})
					}
				})
		},

		// Test Sequence Modal Methods
		showTestSequenceModal() {
			if (!this.testSequenceModal) {
				this.testSequenceModal = new Modal(document.getElementById('testSequenceModal'))
			}
			this.showRawResponse = false
			this.showThumbnail = false
			this.testSequenceModal.show()
		},

		async testSequence(sequence) {
			this.$set(sequence, 'testing', true)
			this.testSequenceLoading = true
			this.testSequenceResponse = null
			this.showThumbnail = false

			try {
				let endpoint
				if (sequence.type === 'postcard') {
					endpoint = '/sequenced-campaign-lob/send-postcard-test'
				} else if (sequence.type === 'letter') {
					endpoint = '/sequenced-campaign-lob/send-letter-test'
				} else {
					throw new Error('Invalid sequence type for testing')
				}

				const response = await restAPI.post(endpoint, {
					sequence_id: sequence.id,
				})

				this.testSequenceResponse = response.data
				this.showTestSequenceModal()
				
				// Show thumbnail after 10 seconds if there's a thumbnail in the response
				if (this.testSequenceResponse.data && this.testSequenceResponse.data.thumbnail) {
					setTimeout(() => {
						this.showThumbnail = true
					}, 10000)
				}
			} catch (error) {
				console.error('Error testing sequence:', error)
				this.testSequenceResponse = {
					success: false,
					error: error.response?.data?.message || error.message || 'Unknown error occurred',
					response: error.response?.data || null,
				}
				this.showTestSequenceModal()
			} finally {
				this.$set(sequence, 'testing', false)
				this.testSequenceLoading = false
			}
		},

		calculateItemCost(item) {
			if (!item.type) return 0

			let total = 0
			let additionalPageCost = 0

			// LOB Pricing (based on campaigns edit screen)
			const pricing = {
				postcard: {
					'4x6': { usps_first_class: 0.513, usps_standard: 0.513 },
					'6x9': { usps_first_class: 0.554, usps_standard: 0.554 },
					'6x11': { usps_first_class: 0.781, usps_standard: 0.781 },
				},
				letter: {
					black: { usps_first_class: 0.588, usps_standard: 0.548 },
					color: { usps_first_class: 0.761, usps_standard: 0.578 },
					additionalBlack: { usps_first_class: 0.1, usps_standard: 0.1 },
					additionalColor: { usps_first_class: 0.2, usps_standard: 0.2 },
				},
			}

			if (item.type === 'letter') {
				// Fix color type determination: '1' means Yes (color), '0' means No (black)
				const colorType = item.letter_color === '1' || item.letter_color === 1 ? 'color' : 'black'
				total = pricing.letter[colorType][item.mail_type] || 0
				additionalPageCost = pricing.letter[`additional${colorType.charAt(0).toUpperCase() + colorType.slice(1)}`][item.mail_type] || 0

				// Extra services
				if (item.letter_extra_service === 'certified') {
					total += 5.75
				} else if (item.letter_extra_service === 'certified_return_receipt') {
					total += 5.75 + 2.2
				}

				// Calculate pages cost
				let pages = item.letter_pages_count || 1
				if (item.letter_double_sided === '1' || item.letter_double_sided === 1) {
					pages = Math.ceil(pages / 2)
				}
				const additionalPagesCost = (pages - 1) * additionalPageCost
				total += additionalPagesCost
			} else if (item.type === 'postcard') {
				total = pricing.postcard[item.postcard_size][item.mail_type] || 0
			}

			return parseFloat(total.toFixed(3))
		},
	},
	mounted() {
		// Close dropdown when clicking outside
		document.addEventListener('click', e => {
			if (!this.$el.contains(e.target)) {
				this.showAddDropdown = false
			}
		})
	},
}
</script>

<style scoped>
.auto-campaign-sequences-wrapper {
	min-height: 100vh;
	background-color: #f8f9fa;
}

.auto-campaign-sequences-page {
	padding: 20px 0;
}

.back-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: #ffffff;
	border: 1px solid #dee2e6;
	color: #6c757d;
	text-decoration: none;
	transition: all 0.2s ease;
}

.back-btn:hover {
	background-color: #f8f9fa;
	color: #495057;
	border-color: #adb5bd;
}

.page-title {
	font-size: 1.75rem;
	font-weight: 600;
	color: #212529;
	margin-bottom: 0.25rem;
}

.page-subtitle {
	color: #6c757d;
	margin-bottom: 0;
	font-size: 0.95rem;
}

.loading-state {
	background: white;
	border-radius: 12px;
	margin: 20px 0;
}

.section-title {
	font-size: 1.1rem;
	font-weight: 600;
	color: #374151;
	margin-bottom: 16px;
}

.form-label {
	font-weight: 500;
	color: #374151;
	margin-bottom: 6px;
}

.form-control:not(textarea),
.form-select {
	border-radius: 8px;
	border: 1px solid #d1d5db;
	padding: 10px 14px;
	font-size: 0.95rem;
	height: 44px; /* Consistent height for inputs and selects */
	line-height: 1.5;
}

.form-control textarea,
textarea.form-control {
	border-radius: 8px;
	border: 1px solid #d1d5db;
	padding: 10px 14px;
	font-size: 0.95rem;
	height: 88px; /* 2x the height of text inputs */
	line-height: 1.5;
	resize: vertical; /* Allow vertical resize only */
}

.form-control:focus,
.form-select:focus,
textarea.form-control:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Keep disabled sequence start at input text black */
.form-control[type='datetime-local']:disabled {
	color: #000000 !important;
	background-color: #f8f9fa;
}

/* Campaign Automation Badge - Compact design attached to campaign card */
.campaign-automation-badge {
	background: linear-gradient(135deg, #3b82f6 0%, #10b981 100%);
	color: white;
	padding: 12px 16px;
	border-radius: 8px 8px 0 0;
	box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
	position: relative;
	margin-bottom: -1px; /* Attach to campaign card */
	z-index: 2;
}

.automation-content {
	text-align: center;
}

.automation-header {
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 0.875rem;
	font-weight: 600;
	margin-bottom: 4px;
}

.automation-title {
	flex: 1;
}

.automation-description {
	font-size: 0.75rem;
	font-weight: 400;
	opacity: 0.9;
	line-height: 1.2;
	max-width: 100%;
	word-wrap: break-word;
}

/* Main Content Layout */
.main-content-wrapper {
	min-height: calc(100vh - 200px);
}

/* Timeline Section */
.timeline-section {
	width: 100%;
}

.timeline-section.timeline-full-width {
	max-width: 1200px;
	margin: 0 auto;
}

.timeline-container {
	padding: 0 0px;
}

.timeline-centered {
	max-width: 1200px;
	margin: 0 auto;
}

.timeline-header {
	padding: 20px 20px 0 20px;
	margin-bottom: 20px;
}

.add-item-btn {
	border-radius: 8px;
	font-weight: 500;
	padding: 8px 16px;
}

/* Add Item Dropdown */
.add-item-dropdown {
	margin-bottom: 16px;
}

.dropdown-menu {
	border-radius: 8px;
	box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
	border: 1px solid #e5e7eb;
	padding: 8px 0;
}

.dropdown-item {
	display: flex;
	align-items: center;
	padding: 10px 16px;
	font-size: 0.95rem;
	color: #374151;
	border: none;
	background: none;
	width: 100%;
	text-align: left;
	transition: background-color 0.2s ease;
}

.dropdown-item:hover {
	background-color: #f3f4f6;
}

/* Timeline Styles - Based on Hand-drawn Design */
.sequence-timeline {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 20px 0px;
}

/* Timeline Start Trigger */
.timeline-start {
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-bottom: 20px;
}

/* Timeline Connector Lines with Arrow */
.timeline-connector {
	width: 2px;
	height: 40px;
	background-color: #374151;
	margin: 10px 0;
	position: relative;
}

.timeline-connector::after {
	content: '';
	position: absolute;
	bottom: -5px;
	left: -4px;
	width: 0;
	height: 0;
	border-left: 5px solid transparent;
	border-right: 5px solid transparent;
	border-top: 8px solid #374151;
}

/* Sequence Start Date Display */
.sequence-start-date {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 8px 16px;
	margin: 5px 0;
	border-radius: 12px;
	font-size: 0.85rem;
	font-weight: 500;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	background: #f8fafc;
	/* border: 1px solid #e2e8f0; */
	color: #475569;
}

.sequence-start-date.time-missing {
	background: #fef3c7;
	border-color: #f59e0b;
	color: #92400e;
}

.sequence-start-date.time-set {
	background: #ecfdf5;
	border-color: #10b981;
	color: #047857;
}

/* Delivery Indicator */
.delivery-indicator {
	display: flex;
	align-items: center;
	margin-left: 8px;
	opacity: 0.7;
	font-size: 0.8rem;
}

.sequence-start-date.time-missing .delivery-indicator {
	color: #92400e;
}

.sequence-start-date.time-set .delivery-indicator {
	color: #047857;
}

/* Connected styling for postcard items */
.sequence-start-date.postcard-node.time-set {
	background: #f0f9ff;
	border-color: #06b6d4;
	color: #0c4a6e;
	margin-top: -5px; /* Pull closer to card */
	border-top: none; /* Remove top border for connection effect */
	border-top-left-radius: 0;
	border-top-right-radius: 0;
	box-shadow: 0 2px 8px rgba(6, 182, 212, 0.1);
}

.sequence-start-date.postcard-node.time-set .delivery-indicator {
	color: #0c4a6e;
}

/* Connected styling for email items */
.sequence-start-date.email-node.time-set {
	background: #eff6ff;
	border-color: #3b82f6;
	color: #1e40af;
	margin-top: -5px;
	border-top: none;
	border-top-left-radius: 0;
	border-top-right-radius: 0;
	box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}

.sequence-start-date.email-node.time-set .delivery-indicator {
	color: #1e40af;
}

/* Connected styling for letter items */
.sequence-start-date.letter-node.time-set {
	background: #f3f0ff;
	border-color: #7c3aed;
	color: #5b21b6;
	margin-top: -5px;
	border-top: none;
	border-top-left-radius: 0;
	border-top-right-radius: 0;
	box-shadow: 0 2px 8px rgba(124, 58, 237, 0.1);
}

.sequence-start-date.letter-node.time-set .delivery-indicator {
	color: #5b21b6;
}

/* Connected styling for letter items with missing time */
.sequence-start-date.letter-node.time-missing {
	background: #fef3c7;
	border-color: #f59e0b;
	color: #92400e;
	margin-top: -5px;
	border-top: none;
	border-top-left-radius: 0;
	border-top-right-radius: 0;
	box-shadow: 0 2px 8px rgba(245, 158, 11, 0.1);
}

.sequence-start-date.letter-node.time-missing .delivery-indicator {
	color: #92400e;
}

/* Connected styling for postcard items with missing time */
.sequence-start-date.postcard-node.time-missing {
	background: #fef3c7;
	border-color: #f59e0b;
	color: #92400e;
	margin-top: -5px;
	border-top: none;
	border-top-left-radius: 0;
	border-top-right-radius: 0;
	box-shadow: 0 2px 8px rgba(245, 158, 11, 0.1);
}

.sequence-start-date.postcard-node.time-missing .delivery-indicator {
	color: #92400e;
}

/* Connected styling for email items with missing time */
.sequence-start-date.email-node.time-missing {
	background: #fef3c7;
	border-color: #f59e0b;
	color: #92400e;
	margin-top: -5px;
	border-top: none;
	border-top-left-radius: 0;
	border-top-right-radius: 0;
	box-shadow: 0 2px 8px rgba(245, 158, 11, 0.1);
}

.sequence-start-date.email-node.time-missing .delivery-indicator {
	color: #92400e;
}

/* Timeline Items */
.timeline-items {
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
}

.timeline-item {
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	margin-bottom: 0;
}

/* Add Step Section */
.timeline-add-item {
	display: flex;
	flex-direction: column;
	align-items: center;
	position: relative;
}

.add-step-circle {
	width: 50px;
	height: 50px;
	border-radius: 50%;
	background-color: #ffffff;
	border: 2px dashed #d1d5db;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.3s ease;
	font-size: 1.2rem;
	color: #9ca3af;
	margin: 10px 0;
}

.add-step-circle:hover {
	border-color: #3b82f6;
	border-style: solid;
	background-color: #f8f9ff;
	color: #3b82f6;
	box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.add-step-circle.active {
	border-color: #3b82f6;
	border-style: solid;
	background-color: #3b82f6;
	color: #ffffff;
	box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

/* Add Item Dropdown */
.add-item-dropdown {
	position: absolute;
	top: 50%;
	left: calc(100% + 15px);
	transform: translateY(-50%);
	z-index: 10;
}

.dropdown-options {
	background-color: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
	padding: 8px 0;
	min-width: 160px;
}

.dropdown-option {
	display: flex;
	align-items: center;
	padding: 10px 16px;
	font-size: 0.9rem;
	color: #374151;
	border: none;
	background: none;
	width: 100%;
	text-align: left;
	cursor: pointer;
	transition: background-color 0.2s ease;
}

.dropdown-option:hover {
	background-color: #f3f4f6;
}

/* Timeline End */
.timeline-end {
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 20px;
	width: 100%;
}

.timeline-end-circle {
	width: 60px;
	height: 60px;
	border-radius: 50%;
	background-color: #ffffff;
	border: 2px solid #374151;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 600;
	color: #374151;
	font-size: 0.9rem;
	transition: all 0.3s ease;
}

/* Expanded circle for active campaign with last item display */
.timeline-end-circle.timeline-end-expanded {
	width: 212px;
	height: 60px;
	border-radius: 30px;
	border-color: #10b981;
	background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);
	box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
}

/* Enhanced last item display */
.last-item-display {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	width: 100%;
	justify-content: center;
}

.last-item-display .fad {
	font-size: 1.6rem;
	flex-shrink: 0;
}

.last-item-text {
	line-height: 1.1;
	text-align: left;
	flex: 1;
}

.last-item-text .fw-bold {
	font-size: 0.75rem;
	color: #374151;
	margin-bottom: 1px;
}

.last-item-text .text-muted {
	font-size: 0.7rem;
	color: #6b7280;
}

.item-config h4 {
	font-size: 1.2rem;
	font-weight: 600;
	color: #374151;
	margin-bottom: 8px;
	text-transform: capitalize;
}

.config-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
	padding-bottom: 16px;
	border-bottom: 1px solid #e5e7eb;
}

.config-header h4 {
	margin-bottom: 0;
}

.config-actions {
	display: flex;
	align-items: center;
	gap: 12px;
}

.item-type-badge {
	display: flex;
	align-items: center;
	padding: 6px 12px;
	border-radius: 20px;
	font-size: 0.8rem;
	font-weight: 500;
	border: 1px solid #e5e7eb;
}

.item-type-badge.email-node {
	background-color: #eff6ff;
	color: #1e40af;
	border-color: #3b82f6;
}

.item-type-badge.postcard-node {
	background-color: #f0f9ff;
	color: #0c4a6e;
	border-color: #06b6d4;
}

.item-type-badge.letter-node {
	background-color: #f3f0ff;
	color: #7c3aed;
	border-color: #7c3aed;
}

.campaign-badge {
	background-color: #f1f5f9;
	color: #475569;
	border-color: #64748b;
}

.config-form {
	max-width: 500px;
}

.config-placeholder {
	background-color: #f9fafb;
	border: 1px dashed #d1d5db;
	border-radius: 8px;
	padding: 24px;
	text-align: center;
	margin-top: 24px;
}

/* Upload Placeholders */
.upload-placeholder {
	background-color: #f8f9fa;
	border: 2px dashed #dee2e6;
	border-radius: 8px;
	padding: 20px;
	text-align: center;
	cursor: pointer;
	transition: all 0.2s ease;
	color: #6c757d;
}

.upload-placeholder:hover {
	border-color: #3b82f6;
	background-color: #f8f9ff;
	color: #3b82f6;
}

.pdf-upload-section {
	border-radius: 8px;
	overflow: hidden;
}

.uploaded-file-info {
	display: flex;
	align-items: center;
	padding: 12px 16px;
	background-color: #f8f9fa;
	border: 1px solid #dee2e6;
	border-radius: 6px;
}

/* Postcard Image Upload */
.postcard-image-upload {
	border-radius: 8px;
	overflow: hidden;
	min-height: 120px;
}

.uploaded-image-preview {
	position: relative;
	display: inline-block;
}

.img-preview {
	max-width: 100%;
	max-height: 120px;
	border-radius: 6px;
	border: 1px solid #dee2e6;
}

.uploaded-image-preview .btn {
	position: absolute;
	top: -8px;
	right: -8px;
	width: 24px;
	height: 24px;
	border-radius: 50%;
	padding: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}

/* Items Info Section */
.items-info-section {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 16px;
	background-color: #f8f9fa;
	border: 1px solid #dee2e6;
	border-radius: 8px;
}

.items-count {
	display: flex;
	align-items: baseline;
	gap: 8px;
}

.items-count-number {
	font-size: 1.5rem;
	font-weight: 600;
	color: #374151;
}

.items-count-label {
	font-size: 0.9rem;
	color: #6b7280;
	font-weight: 500;
}

/* Enhanced Campaign Card */
.timeline-campaign-card {
	background-color: #ffffff;
	/* border: 2px solid #3b82f6; */
	border-radius: 16px;
	padding: 20px;
	max-width: 700px;
	width: 100%;
	cursor: pointer;
	transition: all 0.3s ease;
	box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
	position: relative;
	z-index: 2;
}

.timeline-campaign-card:hover {
	box-shadow: 0 6px 20px rgba(59, 130, 246, 0.2);
	transform: translateY(-2px);
}

.timeline-campaign-card.active {
	border-color: #1d4ed8;
	box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
}

.campaign-card-header {
	display: flex;
	align-items: flex-start;
	gap: 16px;
	/* margin-bottom: 16px; */
}

.campaign-icon {
	width: 50px;
	height: 50px;
	background-color: #eff6ff;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1.5rem;
	color: #3b82f6;
	flex-shrink: 0;
}

.campaign-info {
	flex-grow: 1;
}

.campaign-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1f2937;
}

.campaign-subtitle {
	color: #6b7280;
	font-size: 0.9rem;
	line-height: 1.4;
}

.campaign-badges {
	display: flex;
	flex-direction: column;
	gap: 6px;
	flex-shrink: 0;
	align-items: flex-end;
}

.stats-row {
	display: flex;
	gap: 12px;
	align-items: center;
}

.stat-item-inline {
	display: flex;
	align-items: center;
	gap: 2px;
	font-size: 0.75rem;
	color: #6b7280;
	white-space: nowrap;
}

.stat-item-inline .stat-number {
	font-weight: 600;
	color: #374151;
	font-size: 0.75rem;
}

.stat-item-inline .stat-label {
	font-size: 0.7rem;
	color: #9ca3af;
	margin-left: 1px;
}

.stat-item-inline i {
	color: #6b7280;
	font-size: 0.75rem;
}

/* Outline badge styles */
.badge-outline {
	background: transparent !important;
	font-size: 0.7rem;
	padding: 2px 8px;
	font-weight: 500;
}

.badge-outline-orange {
	color: #ea580c;
	border: 1px solid #ea580c;
}

.badge-outline-green {
	color: #16a34a;
	border: 1px solid #16a34a;
}

.badge-outline-gray {
	color: #6b7280;
	border: 1px solid #6b7280;
}

.badge-outline-blue {
	color: #2563eb;
	border: 1px solid #2563eb;
}

.campaign-card-stats {
	display: flex;
	gap: 24px;
	padding-top: 16px;
	border-top: 1px solid #f3f4f6;
	justify-content: center;
	align-items: center;
}

.campaign-card-actions {
	display: flex;
	justify-content: center;
	gap: 12px;
	/* padding-top: 12px; */
	flex-wrap: wrap;
}

.campaign-card-actions .btn {
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	transition: all 0.2s ease;
}

/* Smaller error badge for check button */
.error-badge-small {
	font-size: 0.72rem;
	padding: 0.24em 0.48em;
	min-width: 1.44em;
	height: 1.44em;
	line-height: 1;
	font-weight: 700;
}

.stat-item {
	text-align: center;
}

.stat-number {
	display: block;
	font-size: 1.1rem;
	font-weight: 600;
	color: #374151;
}

.stat-label {
	font-size: 0.7rem;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

/* Enhanced Timeline Cards */
.timeline-sequence-card-enhanced {
	position: relative; /* For absolute positioned indicators */
	background-color: #ffffff;
	/* border: 2px solid #374151; */
	border-radius: 16px;
	padding: 20px;
	max-width: 600px;
	width: 100%;
	cursor: pointer;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.timeline-sequence-card-enhanced:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
	transform: translateY(-2px);
}

.timeline-sequence-card-enhanced.email-node {
	border-color: #3b82f6;
	box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}

.timeline-sequence-card-enhanced.postcard-node {
	border-color: #06b6d4;
	box-shadow: 0 2px 8px rgba(6, 182, 212, 0.1);
}

.timeline-sequence-card-enhanced.letter-node {
	border-color: #7c3aed;
	box-shadow: 0 2px 8px rgba(124, 58, 237, 0.1);
}

.card-header-enhanced {
	display: flex;
	align-items: flex-start;
	gap: 16px;
}

.card-icon-enhanced {
	width: 45px;
	height: 45px;
	background-color: #f3f4f6;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1.3rem;
	color: #6b7280;
	flex-shrink: 0;
}

.timeline-sequence-card-enhanced.email-node .card-icon-enhanced {
	background-color: #eff6ff;
	color: #3b82f6;
}

.timeline-sequence-card-enhanced.postcard-node .card-icon-enhanced {
	background-color: #f0f9ff;
	color: #06b6d4;
}

.timeline-sequence-card-enhanced.letter-node .card-icon-enhanced {
	background-color: #f3f0ff;
	color: #7c3aed;
}

.card-content-enhanced {
	flex-grow: 1;
}

.card-title-enhanced {
	font-size: 1.1rem;
	font-weight: 600;
	color: #1f2937;
	margin-bottom: 4px;
}

.card-subtitle-enhanced {
	color: #6b7280;
	font-size: 0.85rem;
	line-height: 1.4;
}

.card-header-info {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 8px;
	justify-content: start;
}

.info-badge {
	display: inline-flex;
	align-items: center;
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 0.75rem;
	font-weight: 500;
	line-height: 1;
}

.status-badge {
	border: 1px solid transparent;
	font-weight: 500;
}

.status-badge.status-pending {
	background-color: rgba(217, 119, 6, 0.1);
	color: #d97706;
	border-color: #f59e0b;
}

.status-badge.status-running {
	background-color: rgba(37, 99, 235, 0.1);
	color: #2563eb;
	border-color: #3b82f6;
}

.status-badge.status-completed {
	background-color: rgba(5, 150, 105, 0.1);
	color: #059669;
	border-color: #10b981;
}

.status-badge.status-failed {
	background-color: rgba(220, 38, 38, 0.1);
	color: #dc2626;
	border-color: #ef4444;
}

.status-badge.status-paused {
	background-color: rgba(107, 114, 128, 0.1);
	color: #6b7280;
	border-color: #9ca3af;
}

.status-badge.status-cancelled {
	background-color: rgba(107, 114, 128, 0.1);
	color: #6b7280;
	border-color: #9ca3af;
}

.status-badge.status-scheduled {
	background-color: rgba(8, 145, 178, 0.1);
	color: #0891b2;
	border-color: #06b6d4;
}

.status-badge.status-processing {
	background-color: rgba(37, 99, 235, 0.1);
	color: #2563eb;
	border-color: #3b82f6;
}
.status-badge.status-sending {
	background-color: rgba(37, 99, 235, 0.1);
	color: #2563eb;
	border-color: #3b82f6;
}

.status-badge.status-sent {
	background-color: rgba(5, 150, 105, 0.1);
	color: #059669;
	border-color: #10b981;
}

.status-badge.status-delivered {
	background-color: rgba(5, 150, 105, 0.1);
	color: #059669;
	border-color: #10b981;
}

.recipients-badge {
	background-color: #f3f4f6;
	color: #374151;
	border: 1px solid #d1d5db;
}

.date-badge {
	background-color: #f9fafb;
	color: #4b5563;
	border: 1px solid #e5e7eb;
}

.card-type-badge {
	padding: 4px 12px;
	border-radius: 20px;
	font-size: 0.75rem;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	background-color: #f3f4f6;
	color: #6b7280;
	flex-shrink: 0;
}

.timeline-sequence-card-enhanced.email-node .card-type-badge {
	background-color: #eff6ff;
	color: #3b82f6;
}

.timeline-sequence-card-enhanced.postcard-node .card-type-badge {
	background-color: #f0f9ff;
	color: #06b6d4;
}

.timeline-sequence-card-enhanced.letter-node .card-type-badge {
	background-color: #f3f0ff;
	color: #7c3aed;
}

.card-details-enhanced {
	display: flex;
	gap: 20px;
	margin-bottom: 16px;
	padding-top: 16px;
	border-top: 1px solid #f3f4f6;
}

.detail-item {
	display: flex;
	align-items: center;
	font-size: 0.85rem;
	color: #6b7280;
}

.status-text {
	font-weight: 500;
	text-transform: capitalize;
}

.card-actions-enhanced {
	display: flex;
	gap: 4px;
	justify-content: center;
	opacity: 0;
	transform: translateY(10px);
	transition: all 0.3s ease;
	position: absolute;
	margin-top: 5px;
}

/* Show buttons on card hover */
.timeline-sequence-card-enhanced:hover .card-actions-enhanced {
	opacity: 1;
	transform: translateY(0);
}

/* Circular Buttons */
.btn-circle {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	padding: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: white;
	border: 1px solid #d1d5db;
	color: #6b7280;
	transition: all 0.2s ease;
	font-size: 0.8rem;
}

.btn-circle:hover {
	transform: scale(1.1);
	border-color: #9ca3af;
	color: #374151;
}

.btn-circle:disabled {
	opacity: 1;
	cursor: not-allowed;
	transform: none;
}

.btn-circle.btn-danger {
	border-color: #dc2626;
	color: #dc2626;
}

.btn-circle.btn-danger:hover {
	background-color: #dc2626;
	color: white;
	border-color: #dc2626;
}

/* Card Bottom Indicators - Positioned absolutely */
.card-bottom-indicators {
	position: absolute;
	bottom: -12px; /* Half outside the card */
	left: 0;
	right: 0;
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0 20px; /* Match card padding */
	pointer-events: none; /* Allow clicks to pass through */
}

/* Sequence Status Indicator (Bottom Left) */
.sequence-status-indicator {
	display: flex;
	align-items: center;
	padding: 6px 12px;
	border-radius: 12px;
	font-size: 0.75rem;
	font-weight: 500;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	height: 24px; /* Half-in/half-out effect */
	opacity: 1; /* Always visible */
}

.sequence-status-indicator.status-pending {
	background: #6b7280; /* Solid background */
	color: white;
}

.sequence-status-indicator.status-running {
	background: #3b82f6; /* Solid background */
	color: white;
}
.sequence-status-indicator.status-sending {
	background: #3b82f6; /* Solid background */
	color: white;
	animation: pulse-blue 2s infinite;
	position: relative;
}
.sequence-status-indicator.status-sending::before {
	content: '';
	position: absolute;
	left: 8px;
	top: 50%;
	transform: translateY(-50%);
	width: 6px;
	height: 6px;
	background-color: white;
	border-radius: 50%;
	animation: pulse-dot 1.5s infinite;
}

.sequence-status-indicator.status-completed {
	background: #10b981; /* Solid background */
	color: white;
}

/* Sequence Time Indicator (Bottom Right) */
.sequence-time-indicator {
	display: flex;
	align-items: center;
	padding: 6px 12px;
	border-radius: 12px;
	font-size: 0.75rem;
	font-weight: 500;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	height: 24px; /* Half-in/half-out effect */
	opacity: 1; /* Always visible */
}

.sequence-time-indicator.time-missing {
	background: #fef2f2; /* Solid background */
	color: #dc2626;
	border: 1px solid #fecaca;
}

.sequence-time-indicator.time-set {
	background: #f0fdf4; /* Solid background */
	color: #166534;
	border: 1px solid #bbf7d0;
}

/* Full Width Inline Configuration with Animation */
.inline-config-full-width {
	background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
	/* border: 1px solid #e9ecef; */
	border-radius: 16px;
	padding: 32px;
	margin: 24px 0;
	width: 100%;
	max-width: none;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
	overflow: hidden;

	/* Animation properties */
	opacity: 0;
	transform: translateY(-20px) scale(0.95);
	animation: slideInFade 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
	transform-origin: top center;
}

/* Campaign config specific styling - match sequence-timeline width */
.sequence-timeline .inline-config-full-width.campaign-config {
	width: 100%;
	max-width: 100%;
	margin-left: 0;
	margin-right: 0;
	padding-left: 32px;
	padding-right: 32px;
}

/* Animation keyframes */
@keyframes slideInFade {
	0% {
		opacity: 0;
		transform: translateY(-20px) scale(0.95);
		max-height: 0;
		padding-top: 0;
		padding-bottom: 0;
	}
	50% {
		opacity: 0.6;
		transform: translateY(-10px) scale(0.98);
	}
	100% {
		opacity: 1;
		transform: translateY(0) scale(1);
		max-height: 1000px;
		padding-top: 32px;
		padding-bottom: 32px;
	}
}

.inline-config-full-width .config-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
	padding-bottom: 20px;
	border-bottom: 2px solid #e9ecef;
	animation: headerSlideIn 0.5s ease-out 0.2s both;
}

@keyframes headerSlideIn {
	from {
		opacity: 0;
		transform: translateX(-30px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

.inline-config-full-width .config-header h5 {
	margin: 0;
	color: #1f2937;
	font-weight: 600;
	font-size: 1.25rem;
}

.config-header-left {
	display: flex;
	align-items: center;
	gap: 12px;
}

.config-header-right {
	display: flex;
	align-items: center;
	gap: 8px;
}

.config-form-inline {
	width: 100%;
	animation: formSlideIn 0.6s ease-out 0.3s both;
	overflow: hidden;
}

.sequence-items-view .config-form-inline {
	overflow-x: auto;
	overflow-y: auto;
	max-height: 500px;
	padding-bottom: 30px;
}

.sequence-items-view .table-responsive {
	margin: 0;
	padding: 0;
	border: none;
	border-radius: 8px;
	max-width: 100%;
	overflow: visible;
}

.sequence-items-view .table {
	margin-bottom: 0;
	white-space: nowrap;
	min-width: 600px;
}
.sequence-items-view .table thead th {
	position: sticky;
	top: 0;
	z-index: 10;
	background-color: #212529;
	border-bottom: 2px solid #dee2e6;
}

.sequence-items-view .table td,
.sequence-items-view .table th {
	padding: 8px 12px;
	vertical-align: middle;
}

.sequence-items-view .table td:first-child,
.sequence-items-view .table th:first-child {
	min-width: 60px;
}

.sequence-items-view .table td:nth-child(2),
.sequence-items-view .table th:nth-child(2) {
	min-width: 200px;
}

.sequence-items-view .table td:nth-child(3),
.sequence-items-view .table th:nth-child(3) {
	min-width: 100px;
}

.sequence-items-view .table td:nth-child(4),
.sequence-items-view .table th:nth-child(4),
.sequence-items-view .table td:nth-child(5),
.sequence-items-view .table th:nth-child(5),
.sequence-items-view .table td:nth-child(6),
.sequence-items-view .table th:nth-child(6) {
	min-width: 120px;
}

/* Custom scrollbars for sequence items view */
.sequence-items-view .config-form-inline::-webkit-scrollbar {
	width: 8px;
	height: 8px;
}

.sequence-items-view .config-form-inline::-webkit-scrollbar-track {
	background: #f1f3f4;
	border-radius: 4px;
}

.sequence-items-view .config-form-inline::-webkit-scrollbar-thumb {
	background: #c1c7cd;
	border-radius: 4px;
}

.sequence-items-view .config-form-inline::-webkit-scrollbar-thumb:hover {
	background: #a8b2bd;
}

/* Smooth scrolling */
.sequence-items-view .config-form-inline {
	scroll-behavior: smooth;
}

@keyframes formSlideIn {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.config-form-inline .form-control,
.config-form-inline .form-select,
.config-form-inline .form-label {
	font-size: 0.95rem;
}

.config-form-inline .form-control:focus,
.config-form-inline .form-select:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.upload-control {
	display: flex;
	align-items: center;
	gap: 12px;
}

/* Postcard Upload Styles */

.postcard-upload-section {
	margin-bottom: 0;
}

.postcard-upload-area {
	border: 2px dashed #dee2e6;
	border-radius: 8px;
	text-align: center;
	cursor: pointer;
	transition: all 0.3s ease;
	background-color: #ffffff;
	min-height: 235px;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
}

.postcard-upload-area:hover {
	border-color: #3b82f6;
	background-color: #f8f9ff;
}

.postcard-upload-placeholder {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	color: #6b7280;
}

.postcard-upload-placeholder p {
	margin: 0;
	font-size: 0.9rem;
}

.postcard-preview {
	position: relative;
	width: 100%;
	height: 100%;
	min-height: 120px;
}

.postcard-preview-image {
	width: 100%;
	object-fit: cover;
	border-radius: 6px;
}

.postcard-preview-overlay {
	position: absolute;
	top: 4px;
	right: 4px;
	opacity: 0;
	transition: opacity 0.2s ease;
}

.postcard-preview:hover .postcard-preview-overlay {
	opacity: 1;
}

.postcard-remove-btn {
	width: 24px;
	height: 24px;
	padding: 0 !important;
	border-radius: 50% !important;
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 24px;
	max-width: 24px;
	min-height: 24px;
	max-height: 24px;
	line-height: 1;
	border: none;
}

.postcard-config-fields {
	padding-left: 20px;
}

/* Letter Configuration Styles */
.letter-config-fields {
	padding-right: 20px;
}

.letter-upload-section {
	margin-bottom: 0;
}

.letter-upload-area {
	border: 2px dashed #dee2e6;
	border-radius: 8px;
	text-align: center;
	cursor: pointer;
	transition: all 0.3s ease;
	background-color: #ffffff;
	min-height: 102px;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
}

.letter-upload-area:hover {
	border-color: #7c3aed;
	background-color: #f3f0ff;
}

.letter-upload-placeholder {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	color: #6b7280;
}

.letter-upload-placeholder p {
	margin: 0;
	font-size: 0.9rem;
}

.letter-preview {
	display: flex;
	justify-content: center;
	align-items: center;
	position: relative;
	width: 100%;
	height: 100%;
	min-height: 102px;
}

.pdf-preview-container {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	height: 100%;
	color: #7c3aed;
}

.letter-preview-overlay {
	position: absolute;
	top: 4px;
	right: 4px;
	opacity: 0;
	transition: opacity 0.2s ease;
}

.letter-preview:hover .letter-preview-overlay {
	opacity: 1;
}

.letter-remove-btn {
	width: 24px;
	height: 24px;
	padding: 0 !important;
	border-radius: 50% !important;
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 24px;
	max-width: 24px;
	min-height: 24px;
	max-height: 24px;
	line-height: 1;
	border: none;
}

/* Letter PDF Section Styles */
.letter-pdf-section {
	margin-bottom: 0;
}

.letter-pdf-container {
	display: flex;
	flex-direction: column;
	gap: 16px;
	align-items: stretch;
}

.letter-upload-column {
	flex: 1;
	min-width: 0;
}

.letter-preview-column {
	flex: 1;
	min-width: 0;
}

/* PDF Preview Mini Styles */
.pdf-preview-mini {
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	background: #ffffff;
	overflow: hidden;
	transition: all 0.3s ease;
}

.pdf-preview-mini.expanded {
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 80vw;
	height: 80vh;
	z-index: 1000;
	box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
	border: 2px solid #7c3aed;
}

.pdf-preview-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 12px;
	background: #f8fafc;
	border-bottom: 1px solid #e2e8f0;
}

.preview-label {
	font-size: 0.8rem;
	font-weight: 500;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.expand-btn {
	padding: 2px 6px;
	font-size: 0.7rem;
	line-height: 1;
	min-width: 24px;
	height: 20px;
}

.pdf-mini-viewer {
	height: 250px;
	position: relative;
}

.pdf-preview-mini.expanded .pdf-mini-viewer {
	height: calc(100% - 40px);
}

.pdf-loading-mini {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	color: #7c3aed;
}

.pdf-viewer-wrapper-mini {
	width: 100%;
	height: 100%;
}

.pdf-viewer-mini {
	width: 100%;
	height: 100%;
	border: none;
}

.pdf-fallback-mini {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	background: #f9fafb;
}

.fallback-content-mini {
	text-align: center;
	color: #6b7280;
}

.pdf-icon {
	color: #dc2626;
	margin-bottom: 8px;
}

/* Overlay for expanded preview */
.pdf-preview-mini.expanded::before {
	content: '';
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	z-index: -1;
}

/* Enhanced items info section for compact layout */
.items-info-section-compact {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 8px 12px;
	background-color: #ffffff;
	border: 1px solid #dee2e6;
	border-radius: 8px;
	font-size: 0.85rem;
}

.items-info-section-compact .items-count {
	display: flex;
	align-items: baseline;
	gap: 6px;
}

.items-info-section-compact .items-count-number {
	font-size: 1.1rem;
	font-weight: 600;
	color: #374151;
}

.items-info-section-compact .items-count-label {
	font-size: 0.8rem;
	color: #6b7280;
	font-weight: 500;
}

/* Staggered animation for form elements */
.config-form-inline .row:nth-child(1) {
	animation-delay: 0.4s;
}

.config-form-inline .row:nth-child(2) {
	animation-delay: 0.5s;
}

/* Config Footer */
.config-footer {
	padding-top: 24px;
	margin-top: 24px;
	border-top: 1px solid #e9ecef;
}

/* Pricing Display in Config Footer */
.pricing-display {
	flex: 1;
}

.pricing-info {
	font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
	font-size: 16px;
	font-weight: 600;
	color: #374151;
	letter-spacing: -0.025em;
}

.price-value {
	font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
	font-size: 22px;
	font-weight: 700;
	color: #059669;
	letter-spacing: -0.025em;
}

/* Cost Badge in Sequence Item Cards */
.cost-badge {
	background-color: rgba(13, 110, 253, 0.1); /* Primary blue background */
	color: #0d6efd; /* Primary blue text */
	border: 1px solid rgba(13, 110, 253, 0.2); /* Primary blue border */
	font-weight: 600;
}

/* Section Subtitle */
.section-subtitle {
	font-size: 1rem;
	font-weight: 600;
	color: #374151;
	margin-bottom: 0;
}

/* Form Help Text */
.form-text {
	font-size: 0.75rem;
	color: #6b7280;
	margin-top: 4px;
	line-height: 1.4;
}

.form-text i {
	color: #3b82f6;
	font-size: 0.7rem;
}

/* Error States */
.timeline-sequence-card-enhanced.has-errors {
	border: 2px solid #dc2626 !important;
	background: #fef2f2;
	box-shadow: 0 4px 12px rgba(220, 38, 38, 0.2);
}

/* Offcanvas Customizations */
.offcanvas-footer {
	background: #f9fafb;
}

.error-section {
	margin-bottom: 24px;
}

.error-section-title {
	font-size: 0.95rem;
	font-weight: 600;
	color: #374151;
	margin-bottom: 12px;
	display: flex;
	align-items: center;
	gap: 8px;
}

.sequence-type-badge {
	font-size: 0.7rem;
	padding: 2px 6px;
	border-radius: 4px;
	text-transform: uppercase;
	font-weight: 500;
}

.sequence-type-badge.email {
	background: #dbeafe;
	color: #1e40af;
}

.sequence-type-badge.postcard {
	background: #cffafe;
	color: #0e7490;
}

.sequence-type-badge.letter {
	background: #f3e8ff;
	color: #7c3aed;
}

.error-list {
	margin-bottom: 12px;
}

.error-item {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	padding: 12px;
	background: #fef2f2;
	border: 1px solid #fecaca;
	border-radius: 6px;
	font-size: 0.9rem;
	color: #374151;
	margin-bottom: 8px;
}

.error-item i {
	margin-top: 1px;
	flex-shrink: 0;
}

.ready-status {
	padding: 8px 12px;
	background: #f0fdf4;
	border: 1px solid #bbf7d0;
	border-radius: 6px;
	font-size: 0.9rem;
}

/* Campaign Mode Badge - Behind Campaign Card */
.campaign-mode-badge {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 8px 16px;
	border-radius: 8px;
	border-top-left-radius: 0;
	border-top-right-radius: 0;
	font-size: 0.85rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-top: -2px;
	position: relative;
	z-index: 0;
}

.campaign-mode-badge i {
	font-size: 1rem;
}

.campaign-mode-test {
	background: #fed7aa;
	color: #ea580c;
	box-shadow: 0 2px 8px rgba(234, 88, 12, 0.1);
}

.campaign-mode-live {
	background: #bbf7d0;
	color: #16a34a;
	box-shadow: 0 2px 8px rgba(22, 163, 74, 0.1);
}

.mode-text {
	font-weight: 700;
	text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Compact Table Styles */
.badge-sm {
	font-size: 0.75rem;
	padding: 0.25rem 0.5rem;
}

.table-sm th,
.table-sm td {
	padding: 0.5rem 0.25rem;
	font-size: 0.875rem;
}

.table-sm .text-truncate {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

/* Outline Badge Styles with Improved Status Colors */
.badge-outline-primary {
	color: #2563eb;
	background-color: rgba(37, 99, 235, 0.1);
	border: 1px solid #3b82f6;
	font-weight: 500;
}

.badge-outline-info {
	color: #0891b2;
	background-color: rgba(8, 145, 178, 0.1);
	border: 1px solid #06b6d4;
	font-weight: 500;
}

.badge-outline-secondary {
	color: #6b7280;
	background-color: rgba(107, 114, 128, 0.1);
	border: 1px solid #9ca3af;
	font-weight: 500;
}

.badge-outline-warning {
	color: #d97706;
	background-color: rgba(217, 119, 6, 0.1);
	border: 1px solid #f59e0b;
	font-weight: 500;
}

.badge-outline-success {
	color: #059669;
	background-color: rgba(5, 150, 105, 0.1);
	border: 1px solid #10b981;
	font-weight: 500;
}

.badge-outline-danger {
	color: #dc2626;
	background-color: rgba(220, 38, 38, 0.1);
	border: 1px solid #ef4444;
	font-weight: 500;
}

/* Active Campaign Styles */
.timeline-sequence-card-enhanced.campaign-active {
	cursor: pointer;
	transition: all 0.3s ease;
}

.timeline-sequence-card-enhanced.campaign-active:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Completed sequence styling */
.timeline-sequence-card-enhanced.status-completed {
	border: 2px solid #22c55e !important; /* Green border only */
}

.card-status-active {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 8px 12px;
}

.card-status-active .badge {
	font-size: 0.75rem;
	padding: 0.5rem 1rem;
}

/* Cancel button same size as status badge */
.cancel-btn-small {
	font-size: 0.75rem;
	padding: 0.25rem 0.5rem;
	height: auto;
	line-height: 1.2;
	border-radius: 0.25rem;
}

/* Pulsing blue badge for sending status */
.badge-sending-pulse,
.status-badge.status-sending {
	animation: pulse-blue 2s infinite;
	position: relative;
}

/* Don't pulse the FontAwesome circle icon */

.badge-sending-pulse::before {
	content: '';
	position: absolute;
	left: 6px;
	top: 72%;
	transform: translateY(-50%);
	width: 6px;
	height: 6px;
	background-color: #3b82f6;
	border-radius: 50%;
	animation: pulse-dot 1.5s infinite;
}

@keyframes pulse-blue {
	0% {
		box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
	}
	70% {
		box-shadow: 0 0 0 6px rgba(59, 130, 246, 0);
	}
	100% {
		box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
	}
}

@keyframes pulse-dot {
	0%,
	100% {
		opacity: 1;
		transform: translateY(-50%) scale(1);
	}
	50% {
		opacity: 0.5;
		transform: translateY(-50%) scale(0.8);
	}
}

/* Campaign end styling - adjusted text size */
.campaign-ends-title,
.campaign-ends-date {
	font-size: 1.105em !important; /* 15% smaller than previous 1.3em */
}
</style>
